import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Settings as SettingsIcon, Plus, Edit, Trash2, Search, ToggleLeft, ToggleRight, Palette, RotateCcw, Info, Clock, RefreshCcw, Calendar, Database, CheckCircle2, XCircle, Activity, Wrench, Play } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { toast } from '../../hooks/use-toast';
import { getChannelIcon } from '../../utils/channelIcons';
import AIInsightDesignSelector from './AIInsightDesignSelector';
import 'flag-icons/css/flag-icons.min.css';

// AI Insights Agent Management Tab
const AIInsightsAgentTab = ({ API, token, settings }) => {
  const [agentStatus, setAgentStatus] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [runLoading, setRunLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);

  // Run form
  const [runPlatform, setRunPlatform] = useState('all');
  const [runHours, setRunHours] = useState(72);

  // Delete form
  const [delPlatform, setDelPlatform] = useState('');
  const [delDays, setDelDays] = useState(30);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchStatus = async () => {
    try {
      setStatusLoading(true);
      const res = await fetch(`${API}/admin/ai-agent/status`, { headers });
      if (res.ok) setAgentStatus(await res.json());
    } catch (e) {
      console.error('Failed to fetch agent status:', e);
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchPlatforms = async () => {
    try {
      const res = await fetch(`${API}/platforms`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPlatforms(data.platforms || data || []);
      }
    } catch (e) {
      console.error('Failed to fetch platforms:', e);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchPlatforms();
  }, []);

  const handleRun = async () => {
    setRunLoading(true);
    try {
      const base = runPlatform === 'all'
        ? `${API}/admin/ai-agent/run`
        : `${API}/admin/ai-agent/run/${runPlatform}`;
      const url = `${base}?hours=${runHours}`;
      const res = await fetch(url, { method: 'POST', headers });
      const data = await res.json();
      if (res.ok) {
        toast({ title: 'Agent Run Complete', description: `Created ${data.total_insights || data.insights_created || 0} insights` });
        fetchStatus();
      } else {
        toast({ title: 'Error', description: data.detail || 'Agent run failed', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    } finally {
      setRunLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!delPlatform) {
      toast({ title: 'Error', description: 'Select a platform', variant: 'destructive' });
      return;
    }
    const confirmed = window.confirm(`Delete insights older than ${delDays} days for this platform?`);
    if (!confirmed) return;

    setDeleteLoading(true);
    try {
      const params = new URLSearchParams({ platform_id: delPlatform, days: delDays.toString() });
      const res = await fetch(`${API}/admin/ai-agent/insights?${params}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (res.ok) {
        toast({ title: 'Insights Deleted', description: `Removed ${data.deleted_count} insights` });
      } else {
        toast({ title: 'Error', description: data.detail || 'Delete failed', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const lastRun = agentStatus?.last_run ? new Date(agentStatus.last_run).toLocaleString() : 'Never';
  const nextRun = agentStatus?.next_run ? new Date(agentStatus.next_run).toLocaleString() : 'N/A';

  return (
    <div className="space-y-6">
      {/* Agent Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${agentStatus?.running ? 'bg-green-500' : 'bg-gray-400'}`} />
            AI Insights Agent
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Automated GPT-5.2 analysis of transaction data per platform
          </p>
        </CardHeader>
        <CardContent>
          {statusLoading ? (
            <div className="text-sm text-muted-foreground">Loading status...</div>
          ) : agentStatus ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-muted-foreground">Status</div>
                <div className="text-sm font-medium">{agentStatus.running ? 'Running' : 'Stopped'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Schedule</div>
                <div className="text-sm font-medium">Every {agentStatus.interval_hours}h</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Last Run</div>
                <div className="text-sm font-medium">{lastRun}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Next Run</div>
                <div className="text-sm font-medium">{nextRun}</div>
              </div>
              {agentStatus.last_results?.results && (
                <>
                  <div>
                    <div className="text-xs text-muted-foreground">Total Runs</div>
                    <div className="text-sm font-medium">{agentStatus.total_runs}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Last Insights Created</div>
                    <div className="text-sm font-medium">{agentStatus.last_results.total_insights}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Platforms Analyzed</div>
                    <div className="text-sm font-medium">{agentStatus.last_results.platforms_analyzed}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Duration</div>
                    <div className="text-sm font-medium">{agentStatus.last_results.duration_seconds}s</div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Could not load agent status</div>
          )}
        </CardContent>
      </Card>

      {/* Run Agent On-Demand */}
      <Card>
        <CardHeader>
          <CardTitle>Run Agent On-Demand</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Manually trigger the AI analysis for a specific platform or all platforms
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Platform</label>
              <select
                value={runPlatform}
                onChange={(e) => setRunPlatform(e.target.value)}
                className="h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm w-52"
                data-testid="ai-agent-run-platform"
              >
                <option value="all">All Platforms</option>
                {platforms.map(p => (
                  <option key={p.id} value={p.id}>{p.name || p.id}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Query Data (hours)</label>
              <input
                type="number"
                min={1}
                max={720}
                value={runHours}
                onChange={(e) => setRunHours(parseInt(e.target.value) || 72)}
                className="h-9 w-24 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                data-testid="ai-agent-run-hours"
              />
            </div>
            <button
              onClick={handleRun}
              disabled={runLoading}
              className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              data-testid="ai-agent-run-btn"
            >
              {runLoading ? (
                <><RefreshCcw className="h-4 w-4 animate-spin" /> Running...</>
              ) : (
                <><RefreshCcw className="h-4 w-4" /> Run Now</>
              )}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Delete Insights</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Remove AI-generated insights for a platform older than a specified number of days
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Platform</label>
              <select
                value={delPlatform}
                onChange={(e) => setDelPlatform(e.target.value)}
                className="h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm w-52"
                data-testid="ai-agent-delete-platform"
              >
                <option value="">Select platform...</option>
                {platforms.map(p => (
                  <option key={p.id} value={p.id}>{p.name || p.id}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Older than (days)</label>
              <input
                type="number"
                min={0}
                max={365}
                value={delDays}
                onChange={(e) => setDelDays(parseInt(e.target.value) || 0)}
                className="h-9 w-24 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                data-testid="ai-agent-delete-days"
              />
            </div>
            <button
              onClick={handleDelete}
              disabled={deleteLoading || !delPlatform}
              className="h-9 px-4 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="ai-agent-delete-btn"
            >
              {deleteLoading ? (
                <><Trash2 className="h-4 w-4 animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="h-4 w-4" /> Delete Insights</>
              )}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Settings = () => {
  const [searchParams] = useSearchParams();
  const { user, token, isAdmin, selectedPlatform, backendUrl } = useAuth();
  const { currentPlatform } = usePlatform();
  const { theme, saveTheme, resetTheme, loading: themeLoading, getCurrentPlatformName } = useTheme();
  
  // Get initial tab from URL query param or default to 'profile'
  const initialTab = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Update activeTab when URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);
  
  const [settings, setSettings] = useState({
    platforms: [],
    currencies: [],
    payment_methods: [],
    processors: [],
    transaction_types: [],
    channels: [],
    statuses: [],
    result_codes: [],
    risk_actions: []
  });
  const [envVariables, setEnvVariables] = useState(null);
  const [envLoading, setEnvLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [tempTheme, setTempTheme] = useState(theme);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    code: '', 
    symbol: '',
    logo_url: '',
    status: 'failed_processor',
    countries: [],
    currencies: []
  });
  
  // MFA state
  const [mfaActive, setMfaActive] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  
  // Default screen state
  const [defaultScreen, setDefaultScreen] = useState(null);
  const [defaultScreenLoading, setDefaultScreenLoading] = useState(false);
  
  // Delete platform data state
  const [deletePlatformId, setDeletePlatformId] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingPlatformData, setDeletingPlatformData] = useState(false);
  const [deletionJob, setDeletionJob] = useState(null); // Track background deletion job
  const [deletionHistory, setDeletionHistory] = useState([]); // Deletion history
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Monthly deletion state
  const [monthlyDeletePlatformId, setMonthlyDeletePlatformId] = useState(null);
  const [monthlyDeleteYear, setMonthlyDeleteYear] = useState(new Date().getFullYear());
  const [monthlyDeleteMonth, setMonthlyDeleteMonth] = useState(new Date().getMonth() + 1);
  const [deletingMonthlyData, setDeletingMonthlyData] = useState(false);
  const [monthlyDeleteConfirm, setMonthlyDeleteConfirm] = useState(false);
  
  // Recalculate totals state
  const [recalcPlatformId, setRecalcPlatformId] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  // DB Health panel state
  const [dbHealth, setDbHealth] = useState(null);
  const [dbHealthLoading, setDbHealthLoading] = useState(false);
  const [reindexing, setReindexing] = useState(false);

  // Maintenance data-fix scripts state
  const [dataScripts, setDataScripts] = useState([]);
  const [scriptPlatform, setScriptPlatform] = useState({});   // { [scriptId]: platformId | 'all' }
  const [scriptPreview, setScriptPreview] = useState({});     // { [scriptId]: { affected_count, platform_id, loading } }
  const [runningScript, setRunningScript] = useState(null);   // scriptId currently running
  const [confirmScript, setConfirmScript] = useState(null);   // { script, platformId, affected } | null

  // ---- Close AI insights by date range ----
  const [closeInsStart, setCloseInsStart] = useState('');
  const [closeInsEnd, setCloseInsEnd] = useState('');
  const [closeInsPlatform, setCloseInsPlatform] = useState('all');
  const [closeInsPreview, setCloseInsPreview] = useState(null); // { affected_count, loading } | null
  const [closingIns, setClosingIns] = useState(false);
  const [confirmCloseIns, setConfirmCloseIns] = useState(false);

  // Sample-data generation state
  const [genPlatformId, setGenPlatformId] = useState(null);
  const [genYear, setGenYear] = useState(new Date().getFullYear());
  const [genMonth, setGenMonth] = useState(new Date().getMonth() + 1);
  const [genCount, setGenCount] = useState(2000);
  const [genChannels, setGenChannels] = useState({ web: 70, pos: 20, recurring: 10 });
  const [genConfirm, setGenConfirm] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genJob, setGenJob] = useState(null);        // active/last job being polled
  const [lastGenJobs, setLastGenJobs] = useState([]); // recent generation jobs

  // Dynamic API URL for dev/prod environments
  const API = `${backendUrl}/api`;

  // Fetch deletion history
  const fetchDeletionHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await fetch(`${API}/admin/deletion-jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Combine jobs and audit logs, remove duplicates based on platform_id + timestamp
        const combined = [...(data.jobs || [])];
        
        // Add audit logs that don't have corresponding jobs
        (data.audit_logs || []).forEach(log => {
          const exists = combined.some(job => 
            job.platform_id === log.platform_id && 
            Math.abs(new Date(job.created_at) - new Date(log.timestamp)) < 60000
          );
          if (!exists) {
            combined.push({
              id: log.id,
              platform_id: log.platform_id,
              platform_name: log.platform_name,
              user_email: log.user_email,
              status: 'completed',
              deleted_counts: log.details?.deleted_counts || {},
              created_at: log.timestamp
            });
          }
        });
        
        // Sort by date descending
        combined.sort((a, b) => new Date(b.created_at || b.timestamp) - new Date(a.created_at || a.timestamp));
        setDeletionHistory(combined);
      }
    } catch (error) {
      console.error('Error fetching deletion history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load deletion history when data_management tab is active
  useEffect(() => {
    if (activeTab === 'data_management' && user?.is_admin) {
      fetchDeletionHistory();
      fetchDbHealth();
      fetchDataScripts();
      fetchSampleDataJobs();
    }
  }, [activeTab]);

  // Fetch DB health report (indexes + bootstrap status)
  const fetchDbHealth = async () => {
    try {
      setDbHealthLoading(true);
      const response = await fetch(`${API}/admin/database/health`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDbHealth(await response.json());
      } else {
        setDbHealth(null);
      }
    } catch (error) {
      console.error('Error fetching DB health:', error);
      setDbHealth(null);
    } finally {
      setDbHealthLoading(false);
    }
  };

  // Re-run index creation on demand (idempotent; builds any missing indexes)
  const handleReindex = async () => {
    try {
      setReindexing(true);
      const response = await fetch(`${API}/admin/database/initialize`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        toast({ title: 'Indexing triggered', description: 'Missing indexes are being built in the background. Refreshing health…' });
        // Give the index builds a moment, then refresh the report
        setTimeout(fetchDbHealth, 2500);
      } else {
        const err = await response.json().catch(() => ({}));
        toast({ title: 'Indexing failed', description: err.detail || 'Could not trigger indexing.', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Error triggering reindex:', error);
      toast({ title: 'Indexing failed', description: 'Network error while triggering indexing.', variant: 'destructive' });
    } finally {
      setReindexing(false);
    }
  };

  // ---- Maintenance data-fix scripts ----
  const fetchDataScripts = async () => {
    try {
      const response = await fetch(`${API}/admin/data-scripts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDataScripts(data.scripts || []);
      }
    } catch (error) {
      console.error('Error fetching data scripts:', error);
    }
  };

  // Dry-run preview: how many rows WOULD change (no writes)
  const handlePreviewScript = async (scriptId) => {
    const platformId = scriptPlatform[scriptId] || 'all';
    setScriptPreview(prev => ({ ...prev, [scriptId]: { ...(prev[scriptId] || {}), loading: true } }));
    try {
      const qs = platformId && platformId !== 'all' ? `?platform_id=${platformId}` : '';
      const response = await fetch(`${API}/admin/data-scripts/${scriptId}/preview${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScriptPreview(prev => ({ ...prev, [scriptId]: { affected_count: data.affected_count, action: data.action, platform_id: data.platform_id, loading: false } }));
      } else {
        setScriptPreview(prev => ({ ...prev, [scriptId]: { loading: false } }));
        toast({ title: 'Preview failed', description: 'Could not preview affected rows.', variant: 'destructive' });
      }
    } catch (error) {
      setScriptPreview(prev => ({ ...prev, [scriptId]: { loading: false } }));
      toast({ title: 'Preview failed', description: 'Network error while previewing.', variant: 'destructive' });
    }
  };

  // Execute the script (called after confirmation)
  const handleRunScript = async () => {
    if (!confirmScript) return;
    const { script } = confirmScript;
    const platformId = scriptPlatform[script.id] || 'all';
    setRunningScript(script.id);
    try {
      const qs = platformId && platformId !== 'all' ? `?platform_id=${platformId}` : '';
      const response = await fetch(`${API}/admin/data-scripts/${script.id}/run${qs}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast({ title: 'Script completed', description: `${data.affected_count} document(s) ${data.action === 'deleted' ? 'deleted' : 'updated'}${platformId === 'all' ? ' (all platforms)' : ''}.` });
        setScriptPreview(prev => ({ ...prev, [script.id]: { affected_count: 0, action: prev[script.id]?.action, platform_id: platformId, loading: false } }));
        fetchDataScripts();  // refresh "Last run" indicator
      } else {
        toast({ title: 'Script failed', description: data.detail || 'Could not run script.', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Script failed', description: 'Network error while running script.', variant: 'destructive' });
    } finally {
      setRunningScript(null);
      setConfirmScript(null);
    }
  };

  // ---- Close AI insights by date range ----
  const handlePreviewCloseInsights = async () => {
    if (!closeInsStart || !closeInsEnd) {
      toast({ title: 'Dates required', description: 'Select both a start and end date.', variant: 'destructive' });
      return;
    }
    if (closeInsStart > closeInsEnd) {
      toast({ title: 'Invalid range', description: 'Start date must be on or before end date.', variant: 'destructive' });
      return;
    }
    setCloseInsPreview({ loading: true });
    try {
      const params = new URLSearchParams({ start_date: closeInsStart, end_date: closeInsEnd, preview: 'true' });
      if (closeInsPlatform && closeInsPlatform !== 'all') params.append('platform_id', closeInsPlatform);
      const response = await fetch(`${API}/insights/bulk-close?${params.toString()}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setCloseInsPreview({ affected_count: data.affected_count, loading: false });
      } else {
        setCloseInsPreview(null);
        toast({ title: 'Preview failed', description: data.detail || 'Could not preview insights.', variant: 'destructive' });
      }
    } catch (error) {
      setCloseInsPreview(null);
      toast({ title: 'Preview failed', description: 'Network error while previewing.', variant: 'destructive' });
    }
  };

  const handleCloseInsights = async () => {
    setClosingIns(true);
    try {
      const params = new URLSearchParams({ start_date: closeInsStart, end_date: closeInsEnd });
      if (closeInsPlatform && closeInsPlatform !== 'all') params.append('platform_id', closeInsPlatform);
      const response = await fetch(`${API}/insights/bulk-close?${params.toString()}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast({ title: 'Insights closed', description: `${data.closed_count} insight(s) closed${closeInsPlatform === 'all' ? ' across all platforms' : ''}.` });
        setCloseInsPreview({ affected_count: 0, loading: false });
      } else {
        toast({ title: 'Close failed', description: data.detail || 'Could not close insights.', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Close failed', description: 'Network error while closing insights.', variant: 'destructive' });
    } finally {
      setClosingIns(false);
      setConfirmCloseIns(false);
    }
  };

  // ---- Sample-data generation ----
  const fetchSampleDataJobs = async () => {
    try {
      const response = await fetch(`${API}/admin/sample-data/jobs?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLastGenJobs(data.jobs || []);
        // Resume polling if a job is still in-flight
        const active = (data.jobs || []).find(j => j.status === 'pending' || j.status === 'running');
        if (active && !genJob) {
          setGenJob(active);
          pollSampleDataJob(active.id);
        }
      }
    } catch (error) {
      console.error('Error fetching sample-data jobs:', error);
    }
  };

  const pollSampleDataJob = async (jobId) => {
    try {
      const response = await fetch(`${API}/admin/sample-data/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('status check failed');
      const job = await response.json();
      setGenJob(job);
      if (job.status === 'completed') {
        setGenerating(false);
        const s = job.summary || {};
        toast({
          title: 'Sample data generated',
          description: `Inserted ${s.inserted_count || 0} transactions for ${job.platform_name} (${job.month}).`
        });
        fetchSampleDataJobs();
      } else if (job.status === 'failed') {
        setGenerating(false);
        toast({ title: 'Generation failed', description: job.error || 'Unknown error.', variant: 'destructive' });
        fetchSampleDataJobs();
      } else {
        setTimeout(() => pollSampleDataJob(jobId), 2000);
      }
    } catch (error) {
      setTimeout(() => pollSampleDataJob(jobId), 4000);
    }
  };

  const submitGenerateSampleData = async () => {
    if (!genPlatformId) return;
    const w = Number(genChannels.web) || 0;
    const p = Number(genChannels.pos) || 0;
    const r = Number(genChannels.recurring) || 0;
    if (w + p + r <= 0) {
      toast({ title: 'Invalid channel mix', description: 'Channel weights must sum to more than 0.', variant: 'destructive' });
      return;
    }
    setGenerating(true);
    setGenJob(null);
    const month = `${genYear}-${String(genMonth).padStart(2, '0')}`;
    const channels = `web:${w},pos:${p},recurring:${r}`;
    try {
      const qs = `platform_id=${encodeURIComponent(genPlatformId)}&month=${month}&count=${genCount}&channels=${encodeURIComponent(channels)}`;
      const response = await fetch(`${API}/admin/sample-data/generate?${qs}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.job_id) {
        setGenConfirm(false);
        setGenJob({ id: data.job_id, status: 'pending', progress: 0, message: 'Queued…', platform_name: data.platform_name, month });
        pollSampleDataJob(data.job_id);
      } else {
        setGenerating(false);
        toast({ title: 'Could not start generation', description: data.detail || 'Request failed.', variant: 'destructive' });
      }
    } catch (error) {
      setGenerating(false);
      toast({ title: 'Could not start generation', description: 'Network error.', variant: 'destructive' });
    }
  };
  const getScreenLabel = (path) => {
    const screenLabels = {
      '/dashboard': 'Overview',
      '/dashboard/ai-insights': 'AI Insights',
      '/dashboard/transactions': 'Transactions (Table)',
      '/dashboard/transactions-cards': 'Transactions (Cards)',
      '/dashboard/analytics': 'Analytics',
      '/dashboard/customers': 'Customers',
      '/dashboard/merchants': 'Merchants',
      '/dashboard/users': 'User Management',
      '/dashboard/excel-upload': 'Excel Upload',
      '/dashboard/audit-logs': 'Audit Logs',
      '/dashboard/settings': 'Settings'
    };
    return screenLabels[path] || path;
  };

  // Poll for deletion job status
  const pollDeletionJobStatus = async (jobId, platformName) => {
    try {
      const response = await fetch(`${API}/admin/deletion-jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to check job status');
      
      const job = await response.json();
      setDeletionJob(job);
      
      if (job.status === 'completed') {
        toast({
          title: "Platform Data Deleted",
          description: `Successfully deleted all data for ${platformName}. Transactions: ${job.deleted_counts?.transactions || 0}, Customers: ${job.deleted_counts?.customers || 0}, Merchants: ${job.deleted_counts?.merchants || 0}`,
        });
        setDeletingPlatformData(false);
        setDeletePlatformId(null);
        setDeleteConfirmText('');
        setDeletionJob(null);
        // Refresh deletion history
        fetchDeletionHistory();
      } else if (job.status === 'failed') {
        toast({
          variant: "destructive",
          title: "Deletion Failed",
          description: job.error || "Failed to delete platform data. Please try again.",
        });
        setDeletingPlatformData(false);
        setDeletionJob(null);
        // Refresh deletion history
        fetchDeletionHistory();
      } else {
        // Still running, poll again
        setTimeout(() => pollDeletionJobStatus(jobId, platformName), 3000);
      }
    } catch (error) {
      console.error('Error checking deletion job status:', error);
      // Keep polling on error, might be temporary
      setTimeout(() => pollDeletionJobStatus(jobId, platformName), 5000);
    }
  };

  // Delete platform data function
  const handleDeletePlatformData = async (platformId, platformName) => {
    if (deleteConfirmText !== platformName) {
      toast({
        variant: "destructive",
        title: "Confirmation Required",
        description: `Please type "${platformName}" to confirm deletion.`,
      });
      return;
    }
    
    try {
      setDeletingPlatformData(true);
      
      const response = await fetch(`${API}/admin/platforms/${platformId}/data?confirm=true`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete platform data');
      }
      
      const result = await response.json();
      
      // Check if this is a background job or immediate completion
      if (result.status === 'pending' && result.job_id) {
        // Large dataset - start polling for job status
        setDeletionJob({ 
          id: result.job_id, 
          status: 'pending', 
          expected_counts: result.expected_counts,
          platform_name: platformName 
        });
        toast({
          title: "Deletion Started",
          description: `Large dataset detected. Deletion is running in the background. This may take a few minutes...`,
        });
        // Start polling
        pollDeletionJobStatus(result.job_id, platformName);
      } else {
        // Small dataset - immediate completion
        toast({
          title: "Platform Data Deleted",
          description: `Successfully deleted all data for ${platformName}. Transactions: ${result.results?.deleted_counts?.transactions || 0}, Customers: ${result.results?.deleted_counts?.customers || 0}, Merchants: ${result.results?.deleted_counts?.merchants || 0}`,
        });
        
        // Reset state
        setDeletePlatformId(null);
        setDeleteConfirmText('');
        setDeletingPlatformData(false);
        // Refresh deletion history
        fetchDeletionHistory();
      }
      
    } catch (error) {
      console.error('Error deleting platform data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete platform data. Please try again.",
      });
      setDeletingPlatformData(false);
    }
  };

  // Delete monthly data function
  const handleDeleteMonthlyData = async () => {
    if (!monthlyDeletePlatformId || !monthlyDeleteConfirm) {
      toast({
        variant: "destructive",
        title: "Confirmation Required",
        description: "Please select a platform and confirm the deletion.",
      });
      return;
    }
    
    try {
      setDeletingMonthlyData(true);
      
      const response = await fetch(
        `${API}/admin/platforms/${monthlyDeletePlatformId}/monthly-data?year=${monthlyDeleteYear}&month=${monthlyDeleteMonth}&confirm=true`, 
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete monthly data');
      }
      
      const result = await response.json();
      
      const platformName = settings.platforms.find(p => p.id === monthlyDeletePlatformId)?.name || monthlyDeletePlatformId;
      
      toast({
        title: "Monthly Data Deleted",
        description: `Successfully deleted ${result.period} data for ${platformName}. ${result.deleted_transactions} transactions removed. ${result.merchants_recalculated} merchants and ${result.customers_recalculated} customers recalculated.`,
      });
      
      // Reset state
      setMonthlyDeletePlatformId(null);
      setMonthlyDeleteConfirm(false);
      setDeletingMonthlyData(false);
      // Refresh deletion history
      fetchDeletionHistory();
      
    } catch (error) {
      console.error('Error deleting monthly data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete monthly data. Please try again.",
      });
      setDeletingMonthlyData(false);
    }
  };

  // Handle recalculate totals
  const handleRecalculateTotals = async () => {
    if (!recalcPlatformId) return;
    setRecalculating(true);
    try {
      const response = await fetch(`${API}/admin/recalculate-totals?platform_id=${recalcPlatformId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Recalculation failed');
      
      toast({
        title: "Recalculation Complete",
        description: `${data.merchants_recalculated} merchants and ${data.customers_recalculated} customers updated for ${data.platform_name}.`,
      });
      setRecalcPlatformId(null);
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } finally {
      setRecalculating(false);
    }
  };

  // Fetch user's MFA status on mount
  useEffect(() => {
    const fetchMfaStatus = async () => {
      try {
        const response = await fetch(`${API}/users/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          setMfaEnabled(userData.mfa_enabled || false);
          setMfaActive(userData.mfa_active !== false); // Default to true if not set
          setDefaultScreen(userData.default_screen || null);
        }
      } catch (error) {
        console.error('Error fetching MFA status:', error);
      }
    };

    fetchMfaStatus();
  }, [API, token]);

  const tabs = isAdmin ? [
    { id: 'ai_insights_agent', label: 'AI Insights', icon: '🤖' },
    { id: 'ai_insights_design', label: 'AI Insights Design', icon: '💡' },
    { id: 'channels', label: 'Channels', icon: '📡' },
    { id: 'countries', label: 'Countries', icon: '🌍' },
    { id: 'currencies', label: 'Currencies', icon: '💰' },
    { id: 'data_management', label: 'Data Management', icon: '🗑️' },
    { id: 'env_variables', label: 'Environment Variables', icon: '⚙️' },
    { id: 'payment_methods', label: 'Payment Methods', icon: '💳' },
    { id: 'platforms', label: 'Platforms', icon: '🏢' },
    { id: 'processors', label: 'Processors', icon: '🔄' },
    { id: 'result_codes', label: 'Result Codes', icon: '🔢' },
    { id: 'risk_actions', label: 'Risk Actions', icon: '🛡️' },
    { id: 'security', label: 'Security', icon: '🔐' },
    { id: 'statuses', label: 'Statuses', icon: '📈' },
    { id: 'theme', label: 'Theme', icon: '🎨' },
    { id: 'transaction_types', label: 'Transaction Types', icon: '📊' },
    { id: 'profile', label: 'User Profile', icon: '👤' }
  ] : [
    { id: 'profile', label: 'User Profile', icon: '👤' }
  ];

  useEffect(() => {
    if (isAdmin) {
      fetchSettings();
      if (activeTab === 'env_variables') {
        fetchEnvVariables();
      }
    }
  }, [isAdmin, activeTab]);

  // Sync tempTheme with theme when theme changes
  useEffect(() => {
    setTempTheme(theme);
  }, [theme]);

  // Helper function to validate hex color
  const isValidHexColor = (color) => {
    return /^#[0-9A-F]{6}$/i.test(color);
  };

  // Handle theme color change with validation
  const handleThemeColorChange = (field, value) => {
    console.log('[Settings] handleThemeColorChange called:', { field, value });
    
    // Update tempTheme immediately for instant preview
    const newTempTheme = { ...tempTheme, [field]: value };
    setTempTheme(newTempTheme);
    console.log('[Settings] tempTheme updated:', newTempTheme);
    
    // Only save if it's a valid hex color
    if (isValidHexColor(value)) {
      console.log('[Settings] Valid hex color, saving theme...');
      const newTheme = { ...theme, [field]: value };
      saveTheme(newTheme);
    } else {
      console.log('[Settings] Invalid hex color, not saving:', value);
    }
  };

  const fetchEnvVariables = async () => {
    setEnvLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/env-variables`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEnvVariables(data);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load environment variables',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching environment variables:', error);
      toast({
        title: 'Error',
        description: 'Failed to load environment variables',
        variant: 'destructive'
      });
    } finally {
      setEnvLoading(false);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch all setting types
      const promises = tabs.map(async (tab) => {
        try {
          let endpoint, response, data;
          
          if (tab.id === 'platforms') {
            // Special handling for platforms
            response = await fetch(`${backendUrl}/api/platforms`, { headers });
            if (response.ok) {
              data = await response.json();
              return { [tab.id]: data.platforms || [] };
            }
          } else {
            // Handle other settings
            endpoint = tab.id === 'currencies' ? 'currencies' :
                      tab.id === 'countries' ? 'countries' :
                      tab.id === 'payment_methods' ? 'payment-methods' :
                      tab.id === 'processors' ? 'processors' :
                      tab.id === 'transaction_types' ? 'transaction-types' :
                      tab.id === 'statuses' ? 'statuses' :
                      tab.id === 'result_codes' ? 'result-codes' :
                      tab.id === 'risk_actions' ? 'risk-actions' :
                      tab.id === 'channels' ? 'channels' :
                      tab.id;
            
            response = await fetch(`${backendUrl}/api/admin/settings/${endpoint}`, { headers });
            if (response.ok) {
              data = await response.json();
              return { [tab.id]: data[tab.id] || [] };
            }
          }
          
          return { [tab.id]: [] };
        } catch (error) {
          console.error(`Error fetching ${tab.id}:`, error);
          return { [tab.id]: [] };
        }
      });

      const results = await Promise.all(promises);
      const newSettings = results.reduce((acc, result) => ({ ...acc, ...result }), {});
      setSettings(newSettings);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load transaction settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };



  const handleCreate = () => {
    setEditingItem(null);
    if (activeTab === 'platforms') {
      setFormData({ 
        name: '', 
        description: '', 
        default_currency: '',
        countries: [], 
        currencies: [] 
      });
    } else {
      setFormData({ code: '', name: '', symbol: '', logo_url: '', status: 'failed_processor' });
    }
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    if (activeTab === 'platforms') {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        platform_url: item.platform_url || '',
        timezone: item.timezone || 'UTC',
        default_currency: item.default_currency || '',
        countries: item.countries || [],
        currencies: item.currencies || []
      });
    } else {
      setFormData({
        code: item.code || '',
        name: item.name || '',
        symbol: item.symbol || '',
        logo_url: item.logo_url || '',
        status: item.status || 'failed_processor',
        alpha3: item.alpha3 || ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      let endpoint, response, requestData;

      if (activeTab === 'platforms') {
        // Handle platform operations
        endpoint = editingItem ? `admin/platforms/${editingItem.id}` : 'admin/platforms';
        requestData = {
          name: formData.name,
          description: formData.description,
          platform_url: formData.platform_url,
          timezone: formData.timezone || 'UTC',
          default_currency: formData.default_currency || null,
          countries: formData.countries,
          currencies: formData.currencies
        };
      } else {
        // Handle other settings
        const settingsType = activeTab === 'currencies' ? 'currencies' :
                  activeTab === 'countries' ? 'countries' :
                  activeTab === 'payment_methods' ? 'payment-methods' :
                  activeTab === 'processors' ? 'processors' :
                  activeTab === 'transaction_types' ? 'transaction-types' :
                  activeTab === 'statuses' ? 'statuses' :
                  activeTab === 'result_codes' ? 'result-codes' :
                  activeTab === 'risk_actions' ? 'risk-actions' :
                  activeTab === 'channels' ? 'channels' :
                  activeTab;
        endpoint = editingItem 
          ? `admin/settings/${settingsType}/${editingItem.id}`
          : `admin/settings/${settingsType}`;
        requestData = {
          code: formData.code,
          name: formData.name
        };
        // Only include symbol for currencies
        if (activeTab === 'currencies' && formData.symbol) {
          requestData.symbol = formData.symbol;
        }
        // Only include logo_url for payment_methods and processors
        if ((activeTab === 'payment_methods' || activeTab === 'processors') && formData.logo_url) {
          requestData.logo_url = formData.logo_url;
        }
        // Include status for result_codes
        if (activeTab === 'result_codes') {
          requestData.status = formData.status || 'failed_processor';
        }
        // Include alpha3 for countries (auto-derived server-side when blank)
        if (activeTab === 'countries' && formData.alpha3) {
          requestData.alpha3 = formData.alpha3.toUpperCase();
        }
      }

      if (editingItem) {
        // Update existing item
        const method = activeTab === 'platforms' ? 'PATCH' : 'PUT';
        response = await fetch(`${backendUrl}/api/${endpoint}`, {
          method,
          headers,
          body: JSON.stringify(requestData)
        });
      } else {
        // Create new item
        response = await fetch(`${backendUrl}/api/${endpoint}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestData)
        });
      }

      if (response.ok) {
        toast({
          title: 'Success',
          description: `${editingItem ? 'Updated' : 'Created'} ${activeTab.replace('_', ' ')} successfully`
        });
        setShowModal(false);
        fetchSettings();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Operation failed');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (item) => {
    console.log('handleDelete called for:', item);
    const confirmed = window.confirm(`Are you sure you want to delete "${item.name}"?`);
    console.log('User confirmation:', confirmed);
    if (!confirmed) return;

    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      let endpoint;
      if (activeTab === 'platforms') {
        endpoint = `admin/platforms/${item.id}`;
      } else {
        const settingsEndpoint = activeTab === 'currencies' ? 'currencies' :
                               activeTab === 'countries' ? 'countries' :
                               activeTab === 'payment_methods' ? 'payment-methods' :
                               activeTab === 'processors' ? 'processors' :
                               activeTab === 'transaction_types' ? 'transaction-types' :
                               activeTab === 'statuses' ? 'statuses' :
                               activeTab === 'result_codes' ? 'result-codes' :
                               activeTab === 'risk_actions' ? 'risk-actions' :
                               activeTab === 'channels' ? 'channels' :
                               activeTab;
        endpoint = `admin/settings/${settingsEndpoint}/${item.id}`;
      }

      const response = await fetch(`${backendUrl}/api/${endpoint}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: `Deleted ${item.name} successfully`
        });
        fetchSettings();
      } else {
        throw new Error('Delete operation failed');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      let endpoint, requestData;
      if (activeTab === 'platforms') {
        endpoint = `admin/platforms/${item.id}`;
        requestData = { active: !item.active };
      } else {
        const settingsEndpoint = activeTab === 'currencies' ? 'currencies' :
                               activeTab === 'countries' ? 'countries' :
                               activeTab === 'payment_methods' ? 'payment-methods' :
                               activeTab === 'processors' ? 'processors' :
                               activeTab === 'transaction_types' ? 'transaction-types' :
                               activeTab === 'statuses' ? 'statuses' :
                               activeTab === 'result_codes' ? 'result-codes' :
                               activeTab === 'risk_actions' ? 'risk-actions' :
                               activeTab === 'channels' ? 'channels' :
                               activeTab;
        endpoint = `admin/settings/${settingsEndpoint}/${item.id}`;
        requestData = { is_active: !item.is_active };
      }

      const method = activeTab === 'platforms' ? 'PATCH' : 'PUT';
      const response = await fetch(`${backendUrl}/api/${endpoint}`, {
        method,
        headers,
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        fetchSettings();
        const currentStatus = activeTab === 'platforms' ? item.active : item.is_active;
        toast({
          title: 'Success',
          description: `${item.name} ${currentStatus ? 'deactivated' : 'activated'} successfully`
        });
      } else {
        throw new Error('Toggle operation failed');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Filter items based on search
  const currentItems = settings[activeTab] || [];
  let filteredItems = currentItems.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    
    if (activeTab === 'platforms') {
      return (
        item.name?.toLowerCase().includes(searchLower) ||
        item.id?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower)
      );
    } else {
      return (
        item.name?.toLowerCase().includes(searchLower) ||
        item.code?.toLowerCase().includes(searchLower)
      );
    }
  });
  
  // Sort result_codes by code ascending
  if (activeTab === 'result_codes') {
    filteredItems = [...filteredItems].sort((a, b) => {
      const codeA = a.code || '';
      const codeB = b.code || '';
      return codeA.localeCompare(codeB, undefined, { numeric: true });
    });
  }

  // Redirect non-admin to profile tab if they try to access admin-only tabs
  if (!isAdmin && activeTab !== 'profile') {
    setActiveTab('profile');
  }

  return (
    <div className="space-y-8 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage currencies, payment methods, transaction types, and other values
          </p>
        </div>
      </div>

      {/* Tabs - Horizontally scrollable on mobile, wrapping on desktop */}
      <div className="border-b overflow-x-auto">
        <nav className="flex space-x-4 sm:space-x-8 min-w-max sm:min-w-0 sm:flex-wrap">
          {tabs.map((tab) => {
            return (
              <button
                key={tab.id}
                data-testid={`settings-tab-${tab.id}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSearchTerm('');
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2 text-xl">{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'ai_insights_agent' ? (
        <AIInsightsAgentTab API={API} token={token} settings={settings} />
      ) : activeTab === 'profile' ? (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-5 w-5 text-blue-600" />
              <h4 className="font-medium text-blue-900">User Profile</h4>
            </div>
            <p className="text-blue-800 text-sm">
              Manage your personal profile settings and preferences.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{user?.name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{user?.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{isAdmin ? 'Administrator' : 'User'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Default Landing Screen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-base font-medium text-gray-900">Initial Screen After Login</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Choose which page you want to see first when you log in to the dashboard.
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <select
                  value={defaultScreen || ''}
                  onChange={async (e) => {
                    const newScreen = e.target.value || null;
                    setDefaultScreenLoading(true);
                    try {
                      const response = await fetch(`${API}/users/me/default-screen`, {
                        method: 'PUT',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ default_screen: newScreen })
                      });
                      if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.detail || 'Failed to update default screen');
                      }
                      setDefaultScreen(newScreen);
                      toast({ title: "Success", description: newScreen ? `Default screen set to ${getScreenLabel(newScreen)}` : "Default screen cleared - will use Overview" });
                    } catch (error) {
                      console.error('Error updating default screen:', error);
                      toast({ variant: "destructive", title: "Error", description: error.message || "Failed to update default screen. Please try again." });
                    } finally {
                      setDefaultScreenLoading(false);
                    }
                  }}
                  disabled={defaultScreenLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="default-screen-select"
                >
                  <option value="">Overview (Default)</option>
                  <option value="/dashboard">Overview</option>
                  <option value="/dashboard/ai-insights">AI Insights</option>
                  <option value="/dashboard/transactions">Transactions (Table)</option>
                  <option value="/dashboard/transactions-cards">Transactions (Cards)</option>
                  <option value="/dashboard/analytics">Analytics</option>
                  <option value="/dashboard/customers">Customers</option>
                  <option value="/dashboard/merchants">Merchants</option>
                  {isAdmin && (
                    <>
                      <option value="/dashboard/users">User Management</option>
                      <option value="/dashboard/excel-upload">Excel Upload</option>
                      <option value="/dashboard/audit-logs">Audit Logs</option>
                      <option value="/dashboard/settings">Settings</option>
                    </>
                  )}
                </select>
                {defaultScreenLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    <span>Saving preference...</span>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  {defaultScreen 
                    ? `Currently set to: ${getScreenLabel(defaultScreen)}`
                    : 'No preference set - Overview will be shown after login'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="capitalize">
              {activeTab === 'theme' ? 'Theme Settings' : `${activeTab.replace('_', ' ')} Management`}
            </CardTitle>
            <div className="flex items-center gap-4">
              {activeTab !== 'theme' && activeTab !== 'env_variables' && (
                <>
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  {/* Add Button */}
                  <button
                    onClick={handleCreate}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                    style={{ 
                      backgroundColor: tempTheme.buttonColor || theme.buttonColor,
                      color: tempTheme.buttonTextColor || theme.buttonTextColor || '#ffffff'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = tempTheme.buttonHoverColor || theme.buttonHoverColor}
                    onMouseLeave={(e) => e.target.style.backgroundColor = tempTheme.buttonColor || theme.buttonColor}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New
                  </button>
                </>
              )}
              {activeTab === 'theme' && (
                <button
                  onClick={resetTheme}
                  className="inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                  style={{ 
                    backgroundColor: tempTheme.buttonSecondaryColor || theme.buttonSecondaryColor,
                    borderColor: tempTheme.borderColor || theme.borderColor,
                    color: tempTheme.buttonSecondaryTextColor || theme.textColor
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = tempTheme.hoverBackgroundColor || theme.hoverBackgroundColor}
                  onMouseLeave={(e) => e.target.style.backgroundColor = tempTheme.buttonSecondaryColor || theme.buttonSecondaryColor}
                  title={`Reset theme for ${getCurrentPlatformName()}`}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset Theme
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {activeTab === 'security' ? (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="h-5 w-5 text-blue-600" />
                  <h4 className="font-medium text-blue-900">Security Settings</h4>
                </div>
                <p className="text-blue-800 text-sm">
                  Manage your account security settings and two-factor authentication.
                </p>
              </div>

              {/* MFA Toggle Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Two-Factor Authentication (MFA)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-900">MFA Status</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        {mfaActive 
                          ? 'Two-factor authentication is currently active and will be required during login.'
                          : 'Two-factor authentication is currently disabled. You will not be prompted for MFA during login.'}
                      </p>
                      {mfaEnabled && (
                        <p className="mt-2 text-xs text-gray-500">
                          Note: Your MFA setup is preserved. You can reactivate it anytime without needing to reconfigure.
                        </p>
                      )}
                      {!mfaEnabled && (
                        <p className="mt-2 text-xs text-orange-600">
                          ⚠️ MFA is not set up. Please complete MFA setup first before you can use this toggle.
                        </p>
                      )}
                    </div>
                    
                    <button
                      onClick={async () => {
                        if (!mfaEnabled) {
                          alert("MFA is not set up. Please complete MFA setup before toggling this setting.");
                          return;
                        }

                        setMfaLoading(true);
                        try {
                          const response = await fetch(`${API}/mfa/toggle-active?active=${!mfaActive}`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            }
                          });

                          if (!response.ok) {
                            throw new Error('Failed to toggle MFA status');
                          }

                          const data = await response.json();
                          setMfaActive(data.mfa_active);
                          
                          alert(`Success: ${data.message}`);
                        } catch (error) {
                          console.error('Error toggling MFA:', error);
                          alert("Failed to toggle MFA status. Please try again.");
                        } finally {
                          setMfaLoading(false);
                        }
                      }}
                      disabled={mfaLoading || !mfaEnabled}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        mfaActive
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                      } ${(!mfaEnabled || mfaLoading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {mfaLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Updating...</span>
                        </>
                      ) : mfaActive ? (
                        <>
                          <ToggleRight className="h-5 w-5" />
                          <span>Active</span>
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="h-5 w-5" />
                          <span>Inactive</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Additional Security Info */}
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">How it works:</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span><strong>Active:</strong> You'll need to enter your authenticator code every time you log in.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span><strong>Inactive:</strong> Login will proceed without MFA verification.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span>Your MFA configuration remains saved and can be reactivated anytime.</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : activeTab === 'theme' ? (
            <div className="space-y-8">
              {/* Platform Context Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="h-5 w-5 text-blue-600" />
                  <h4 className="font-medium text-blue-900">Theme Context</h4>
                </div>
                <p className="text-blue-800 text-sm">
                  You are currently customizing the theme for: <strong>{getCurrentPlatformName()}</strong>
                  {selectedPlatform === 'all' && (
                    <span className="block mt-1 text-blue-700">
                      This is the default theme that will be used for all platforms unless they have their own custom theme.
                    </span>
                  )}
                  {selectedPlatform !== 'all' && (
                    <span className="block mt-1 text-blue-700">
                      Changes will only apply to this specific platform. Switch to "All Platforms" to edit the default theme.
                    </span>
                  )}
                </p>
              </div>

              {/* Theme Settings Form */}
              <div className="space-y-8">
                
                {/* Typography Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span>📝</span> Typography
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Font Family */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Font Family</label>
                      <select
                        value={theme.fontFamily}
                        onChange={(e) => {
                          const newTheme = { ...theme, fontFamily: e.target.value };
                          saveTheme(newTheme);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="Inter, system-ui, sans-serif">Inter (Default)</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="Helvetica, Arial, sans-serif">Helvetica</option>
                        <option value="'Segoe UI', Tahoma, Geneva, Verdana, sans-serif">Segoe UI</option>
                        <option value="'Roboto', sans-serif">Roboto</option>
                        <option value="'Open Sans', sans-serif">Open Sans</option>
                        <option value="'Lato', sans-serif">Lato</option>
                        <option value="Georgia, serif">Georgia</option>
                        <option value="'Times New Roman', serif">Times New Roman</option>
                      </select>
                    </div>

                    {/* Text Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Text Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.textColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, textColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.textColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, textColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#1f2937"
                        />
                      </div>
                    </div>

                    {/* Text Muted Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Muted Text Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.textMutedColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, textMutedColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.textMutedColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, textMutedColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#6b7280"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Layout Colors Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span>🎨</span> Layout Colors
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Background Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Background Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.backgroundColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, backgroundColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.backgroundColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, backgroundColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#f9fafb"
                        />
                      </div>
                    </div>

                    {/* Card Background Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Card Background</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.cardBackgroundColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, cardBackgroundColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.cardBackgroundColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, cardBackgroundColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#ffffff"
                        />
                      </div>
                    </div>

                    {/* Border Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Border Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.borderColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, borderColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.borderColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, borderColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#e5e7eb"
                        />
                      </div>
                    </div>

                    {/* Sidebar Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Sidebar Background</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.sidebarColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.sidebarColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#1f2937"
                        />
                      </div>
                    </div>

                    {/* Sidebar Text Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Sidebar Text Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.sidebarTextColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarTextColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.sidebarTextColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarTextColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#e2e8f0"
                        />
                      </div>
                    </div>

                    {/* Sidebar Icon Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Sidebar Icon Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.sidebarIconColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarIconColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.sidebarIconColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarIconColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#cbd5e0"
                        />
                      </div>
                    </div>

                    {/* Sidebar Active Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Sidebar Active Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.sidebarActiveColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarActiveColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.sidebarActiveColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarActiveColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#3182ce"
                        />
                      </div>
                    </div>

                    {/* Sidebar Active Text Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Sidebar Active Text Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.sidebarActiveTextColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarActiveTextColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.sidebarActiveTextColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarActiveTextColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#ffffff"
                        />
                      </div>
                    </div>

                    {/* Sidebar Active Icon Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Sidebar Active Icon Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.sidebarActiveIconColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarActiveIconColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.sidebarActiveIconColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, sidebarActiveIconColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#ffffff"
                        />
                      </div>
                    </div>

                    {/* Primary Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Primary Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.primaryColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, primaryColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.primaryColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, primaryColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#3b82f6"
                        />
                      </div>
                    </div>

                    {/* Header Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Header Background</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.headerColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, headerColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.headerColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, headerColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#111827"
                        />
                      </div>
                    </div>

                    {/* Header Text Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Header Text</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.headerTextColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, headerTextColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.headerTextColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, headerTextColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#ffffff"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Interactive Elements Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span>🔗</span> Interactive Elements
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Link Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Link Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.linkColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, linkColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.linkColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, linkColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#3b82f6"
                        />
                      </div>
                    </div>

                    {/* Link Hover Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Link Hover</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.linkHoverColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, linkHoverColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.linkHoverColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, linkHoverColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#2563eb"
                        />
                      </div>
                    </div>

                    {/* Accent Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Accent Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.accentColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, accentColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.accentColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, accentColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#10b981"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Elements Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span>📝</span> Form Elements
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Input Background */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Input Background</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.inputBackgroundColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, inputBackgroundColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.inputBackgroundColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, inputBackgroundColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#ffffff"
                        />
                      </div>
                    </div>

                    {/* Input Border */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Input Border</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.inputBorderColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, inputBorderColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.inputBorderColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, inputBorderColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#d1d5db"
                        />
                      </div>
                    </div>

                    {/* Input Focus */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Input Focus</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.inputFocusColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, inputFocusColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.inputFocusColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, inputFocusColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#3b82f6"
                        />
                      </div>
                    </div>

                    {/* Input Text Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Input Text Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.inputTextColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, inputTextColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.inputTextColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, inputTextColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#1a202c"
                        />
                      </div>
                    </div>

                    {/* Input Placeholder Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Input Placeholder Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.inputPlaceholderColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, inputPlaceholderColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.inputPlaceholderColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, inputPlaceholderColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#a0aec0"
                        />
                      </div>
                    </div>

                    {/* Filter Text Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Filter Text Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.filterTextColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, filterTextColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.filterTextColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, filterTextColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#1a202c"
                        />
                      </div>
                    </div>

                    {/* Filter Label Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Filter Label Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.filterLabelColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, filterLabelColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.filterLabelColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, filterLabelColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#4a5568"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Colors Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span>🚦</span> Status Colors
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Success Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Success</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.successColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, successColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.successColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, successColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#10b981"
                        />
                      </div>
                    </div>

                    {/* Warning Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Warning</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.warningColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, warningColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.warningColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, warningColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#f59e0b"
                        />
                      </div>
                    </div>

                    {/* Error Color */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Error</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={theme.errorColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, errorColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.errorColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, errorColor: e.target.value };
                            saveTheme(newTheme);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#ef4444"
                        />
                      </div>
                    </div>

                    {/* Info Color */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Info</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="color"
                          value={theme.infoColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, infoColor: e.target.value };
                            saveTheme(newTheme);
                            
                          }}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={theme.infoColor}
                          onChange={(e) => {
                            const newTheme = { ...theme, infoColor: e.target.value };
                            saveTheme(newTheme);
                            
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#3b82f6"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Button Colors Section */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span>🔘</span> Button Colors
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Primary Button Background */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Primary Button Background</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={tempTheme.buttonColor || '#3182ce'}
                          onChange={(e) => handleThemeColorChange('buttonColor', e.target.value)}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={tempTheme.buttonColor || '#3182ce'}
                          onChange={(e) => setTempTheme({ ...tempTheme, buttonColor: e.target.value })}
                          onBlur={(e) => handleThemeColorChange('buttonColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#3182ce"
                        />
                      </div>
                    </div>

                    {/* Primary Button Hover */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Primary Button Hover</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={tempTheme.buttonHoverColor || '#2c5aa0'}
                          onChange={(e) => handleThemeColorChange('buttonHoverColor', e.target.value)}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={tempTheme.buttonHoverColor || '#2c5aa0'}
                          onChange={(e) => setTempTheme({ ...tempTheme, buttonHoverColor: e.target.value })}
                          onBlur={(e) => handleThemeColorChange('buttonHoverColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#2c5aa0"
                        />
                      </div>
                    </div>

                    {/* Primary Button Text */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Primary Button Text</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={tempTheme.buttonTextColor || '#ffffff'}
                          onChange={(e) => handleThemeColorChange('buttonTextColor', e.target.value)}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={tempTheme.buttonTextColor || '#ffffff'}
                          onChange={(e) => setTempTheme({ ...tempTheme, buttonTextColor: e.target.value })}
                          onBlur={(e) => handleThemeColorChange('buttonTextColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#ffffff"
                        />
                      </div>
                    </div>

                    {/* Secondary Button Background */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Secondary Button Background</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={tempTheme.buttonSecondaryColor || '#e5e7eb'}
                          onChange={(e) => handleThemeColorChange('buttonSecondaryColor', e.target.value)}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={tempTheme.buttonSecondaryColor || '#e5e7eb'}
                          onChange={(e) => setTempTheme({ ...tempTheme, buttonSecondaryColor: e.target.value })}
                          onBlur={(e) => handleThemeColorChange('buttonSecondaryColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#e5e7eb"
                        />
                      </div>
                    </div>

                    {/* Secondary Button Text */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Secondary Button Text</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={tempTheme.buttonSecondaryTextColor || '#374151'}
                          onChange={(e) => handleThemeColorChange('buttonSecondaryTextColor', e.target.value)}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={tempTheme.buttonSecondaryTextColor || '#374151'}
                          onChange={(e) => setTempTheme({ ...tempTheme, buttonSecondaryTextColor: e.target.value })}
                          onBlur={(e) => handleThemeColorChange('buttonSecondaryTextColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#374151"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                </div>

              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Live Preview</h3>
                <div 
                  className="p-6 rounded-lg border-2 border-dashed border-gray-300 space-y-4"
                  style={{
                    fontFamily: theme.fontFamily,
                    color: theme.textColor,
                    backgroundColor: theme.backgroundColor
                  }}
                >
                  {/* Sample Card */}
                  <div 
                    className="p-6 rounded-lg shadow-sm"
                    style={{ 
                      backgroundColor: theme.cardBackgroundColor,
                      borderColor: theme.borderColor,
                      borderWidth: '1px',
                      borderStyle: 'solid'
                    }}
                  >
                    <h4 className="text-xl font-bold mb-3">Sample Dashboard Card</h4>
                    <p className="mb-4" style={{ color: theme.textMutedColor }}>
                      This preview shows how your dashboard will look with the selected theme.
                    </p>
                    
                    {/* Sample Buttons */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      <button
                        className="px-4 py-2 rounded-md font-medium transition-colors"
                        style={{ 
                          backgroundColor: tempTheme.buttonColor || '#3182ce',
                          color: tempTheme.buttonTextColor || '#ffffff'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = tempTheme.buttonHoverColor || '#2c5aa0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = tempTheme.buttonColor || '#3182ce'}
                      >
                        Primary Button
                      </button>
                      <button
                        className="px-4 py-2 rounded-md font-medium transition-colors"
                        style={{ 
                          backgroundColor: tempTheme.buttonSecondaryColor || '#e5e7eb',
                          color: tempTheme.buttonSecondaryTextColor || '#374151',
                          borderColor: tempTheme.borderColor,
                          borderWidth: '1px',
                          borderStyle: 'solid'
                        }}
                      >
                        Secondary Button
                      </button>
                    </div>
                    
                    {/* Sample Elements */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      <span 
                        className="px-3 py-1 rounded-full text-sm font-medium text-white"
                        style={{ backgroundColor: theme.accentColor }}
                      >
                        Accent Element
                      </span>
                      <span 
                        className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ 
                          backgroundColor: theme.successColor + '20',
                          color: theme.successColor
                        }}
                      >
                        Success Status
                      </span>
                      <span 
                        className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ 
                          backgroundColor: theme.warningColor + '20',
                          color: theme.warningColor
                        }}
                      >
                        Warning Status
                      </span>
                      <span 
                        className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ 
                          backgroundColor: theme.errorColor + '20',
                          color: theme.errorColor
                        }}
                      >
                        Error Status
                      </span>
                    </div>
                    
                    {/* Sample Link */}
                    <a 
                      href="#" 
                      className="font-medium hover:underline"
                      style={{ color: theme.linkColor }}
                      onClick={(e) => e.preventDefault()}
                    >
                      Sample Link Element
                    </a>
                  </div>

                  {/* Sample Navigation */}
                  <div 
                    className="p-4 rounded-lg text-white"
                    style={{ backgroundColor: theme.sidebarColor }}
                  >
                    <h5 className="font-semibold mb-3">Sample Navigation</h5>
                    <div className="space-y-2">
                      <div 
                        className="px-3 py-2 rounded-md font-medium"
                        style={{ 
                          backgroundColor: theme.sidebarActiveColor,
                          color: 'white'
                        }}
                      >
                        Active Menu Item
                      </div>
                      <div 
                        className="px-3 py-2 rounded-md font-medium"
                        style={{ color: theme.sidebarTextColor }}
                      >
                        Regular Menu Item
                      </div>
                    </div>
                  </div>

                  {/* Sample Header */}
                  <div 
                    className="p-4 rounded-lg"
                    style={{ 
                      backgroundColor: theme.headerColor,
                      color: theme.headerTextColor
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <h5 className="font-semibold">Sample Header</h5>
                      <div className="text-sm">Header Content</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          ) : activeTab === 'ai_insights_design' ? (
            <Card>
              <CardHeader>
                <CardTitle>AI Insights Card Design</CardTitle>
                <p className="text-sm text-gray-600 mt-2">
                  Choose how your AI Insights cards should be displayed
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <AIInsightDesignSelector />
              </CardContent>
            </Card>

          ) : activeTab === 'env_variables' ? (
            <div className="space-y-6">
              {envLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-sm text-gray-600">Loading environment variables...</p>
                </div>
              ) : envVariables ? (
                <>
                  {/* Environment Info Banner */}
                  <div className={`rounded-lg p-4 border-2 ${
                    envVariables.environment === 'production' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`text-2xl ${
                        envVariables.environment === 'production' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {envVariables.environment === 'production' ? '🚀' : '🛠️'}
                      </div>
                      <div>
                        <h3 className={`text-lg font-semibold ${
                          envVariables.environment === 'production' ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {envVariables.environment === 'production' ? 'Production Environment' : 'Development Environment'}
                        </h3>
                        <p className={`text-sm ${
                          envVariables.environment === 'production' ? 'text-green-700' : 'text-red-700'
                        }`}>
                          Currently using database: <strong>{envVariables.database}</strong>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Environment Variables Table */}
                  <div className="bg-white rounded-lg border">
                    <div className="px-6 py-4 border-b bg-gray-50">
                      <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        Environment Variables
                      </h4>
                    </div>
                    <div className="divide-y">
                      {Object.entries(envVariables.environment_variables || {}).map(([key, value]) => (
                        <div key={key} className="px-6 py-4 hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm font-semibold text-gray-900">
                                  {key}
                                </span>
                                {value === '***HIDDEN***' && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                                    Secret
                                  </span>
                                )}
                              </div>
                              <div className="font-mono text-sm text-gray-600 break-all">
                                {value || <span className="text-gray-400 italic">Not set</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Security Notice */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">⚠️</span>
                      <div>
                        <h4 className="text-sm font-semibold text-yellow-900 mb-1">Security Notice</h4>
                        <p className="text-sm text-yellow-800">
                          Sensitive values (secrets, API keys, database URLs) are hidden for security. 
                          Only administrators can view environment variables.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">Failed to load environment variables</p>
                </div>
              )}
            </div>
          ) : activeTab === 'data_management' ? (
            <div className="space-y-6">
              {/* Database Health Panel (read-only + re-run indexing) */}
              <Card className="border-indigo-200" data-testid="db-health-card">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <CardTitle className="text-indigo-700 flex items-center gap-2">
                      <Database className="h-5 w-5" />
                      Database Health
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={fetchDbHealth}
                        disabled={dbHealthLoading}
                        data-testid="db-health-refresh-btn"
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
                      >
                        <RefreshCcw className={`h-3.5 w-3.5 ${dbHealthLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                      <button
                        onClick={handleReindex}
                        disabled={reindexing}
                        data-testid="db-health-reindex-btn"
                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {reindexing ? (
                          <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>Indexing…</>
                        ) : (
                          <><Activity className="h-3.5 w-3.5" />Re-run Indexing</>
                        )}
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Confirms production indexing &amp; scaling health without shell access. Indexes are built
                    automatically on every deploy; use <strong>Re-run Indexing</strong> to build any missing ones on demand (idempotent).
                  </p>

                  {dbHealthLoading && !dbHealth ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                      Loading database health…
                    </div>
                  ) : !dbHealth ? (
                    <div className="text-sm text-gray-500 py-4">Unable to load database health. Click Refresh to retry.</div>
                  ) : (
                    <>
                      {/* Summary stat cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="db-health-summary">
                        <div className={`rounded-lg p-3 border ${dbHealth.all_critical_indexes_present ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                          <p className="text-xs text-gray-500 font-medium">Critical Indexes</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            {dbHealth.all_critical_indexes_present
                              ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                              : <XCircle className="h-4 w-4 text-red-600" />}
                            <span className={`text-lg font-bold ${dbHealth.all_critical_indexes_present ? 'text-green-700' : 'text-red-700'}`}>
                              {dbHealth.critical_indexes_present}/{dbHealth.critical_indexes_expected}
                            </span>
                          </div>
                          <p className={`text-xs mt-0.5 ${dbHealth.all_critical_indexes_present ? 'text-green-600' : 'text-red-600'}`}>
                            {dbHealth.all_critical_indexes_present ? 'All present' : 'Action needed'}
                          </p>
                        </div>

                        <div className={`rounded-lg p-3 border ${dbHealth.platform_stats?.bootstrap_complete ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                          <p className="text-xs text-gray-500 font-medium">Platform Stats</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            {dbHealth.platform_stats?.bootstrap_complete
                              ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                              : <Info className="h-4 w-4 text-amber-600" />}
                            <span className="text-lg font-bold text-gray-800">
                              {dbHealth.platform_stats?.fully_recomputed_docs ?? '–'}/{dbHealth.platform_stats?.platforms ?? '–'}
                            </span>
                          </div>
                          <p className="text-xs mt-0.5 text-gray-500">
                            {dbHealth.platform_stats?.bootstrap_complete ? 'Bootstrapped' : `${dbHealth.platform_stats?.incomplete_docs ?? 0} incomplete`}
                          </p>
                        </div>

                        <div className="rounded-lg p-3 border bg-gray-50 border-gray-200">
                          <p className="text-xs text-gray-500 font-medium">Data Size</p>
                          <p className="text-lg font-bold text-gray-800 mt-1">{dbHealth.db_stats?.data_size_mb ?? '–'} MB</p>
                          <p className="text-xs mt-0.5 text-gray-500">Index: {dbHealth.db_stats?.index_size_mb ?? '–'} MB</p>
                        </div>

                        <div className="rounded-lg p-3 border bg-gray-50 border-gray-200">
                          <p className="text-xs text-gray-500 font-medium">Total Indexes</p>
                          <p className="text-lg font-bold text-gray-800 mt-1">{dbHealth.db_stats?.total_indexes ?? '–'}</p>
                          <p className="text-xs mt-0.5 text-gray-500">{dbHealth.db_stats?.objects?.toLocaleString() ?? '–'} objects</p>
                        </div>
                      </div>

                      {/* Per-collection expected indexes */}
                      <div className="space-y-4" data-testid="db-health-collections">
                        {(dbHealth.collections || []).map((coll) => (
                          <div key={coll.collection} className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                              <span className="font-mono text-sm font-semibold text-gray-800">{coll.collection}</span>
                              <span className="text-xs text-gray-500">
                                {coll.document_count?.toLocaleString() ?? '–'} docs · {coll.index_count ?? '–'} indexes
                              </span>
                            </div>
                            <div className="divide-y divide-gray-100">
                              {(coll.expected || []).map((idx, i) => (
                                <div key={i} className="px-4 py-2 flex items-center justify-between gap-3" data-testid={`db-index-${coll.collection}-${i}`}>
                                  <div className="flex items-center gap-2 min-w-0">
                                    {idx.present
                                      ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                                      : (idx.optional
                                          ? <Info className="h-4 w-4 text-amber-500 shrink-0" />
                                          : <XCircle className="h-4 w-4 text-red-600 shrink-0" />)}
                                    <span className="text-sm text-gray-700 truncate">{idx.label}</span>
                                    {idx.optional && (
                                      <span className="text-[10px] uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 shrink-0">optional</span>
                                    )}
                                  </div>
                                  <span className={`text-xs font-medium shrink-0 ${idx.present ? 'text-green-600' : (idx.optional ? 'text-amber-600' : 'text-red-600')}`}>
                                    {idx.present ? 'Present' : 'Missing'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      <p className="text-xs text-gray-400">
                        {dbHealth.environment} · {dbHealth.database} · checked {dbHealth.timestamp ? new Date(dbHealth.timestamp).toLocaleString() : '–'}
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Maintenance Data-Fix Scripts */}
              <Card className="border-amber-200" data-testid="data-scripts-card">
                <CardHeader>
                  <CardTitle className="text-amber-700 flex items-center gap-2">
                    <Wrench className="h-5 w-5" /> Maintenance Scripts
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    One-off data-fix scripts to correct production data. Each is safe &amp; idempotent — use
                    <strong> Preview</strong> to see how many rows would change before you <strong>Run</strong>.
                    Every run is recorded in the Audit Log.
                  </p>

                  {dataScripts.length === 0 ? (
                    <div className="text-sm text-gray-500 py-2" data-testid="data-scripts-empty">No maintenance scripts available.</div>
                  ) : (
                    <div className="space-y-3" data-testid="data-scripts-list">
                      {dataScripts.map((script) => {
                        const platformId = scriptPlatform[script.id] || 'all';
                        const preview = scriptPreview[script.id];
                        return (
                          <div key={script.id} className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid={`data-script-${script.id}`}>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-800 text-sm">{script.name}</span>
                                <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${script.danger === 'high' ? 'bg-red-100 text-red-700' : script.danger === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{script.danger}</span>
                              </div>
                              <p className="text-xs text-gray-600 mt-1">{script.description}</p>
                              <p className="text-xs mt-1.5 flex items-center gap-1.5" data-testid={`data-script-lastrun-${script.id}`}>
                                <Clock className="h-3 w-3 text-gray-400" />
                                {script.last_run ? (
                                  <span className="text-gray-500">
                                    Last run {new Date(script.last_run.at).toLocaleString()} by <span className="font-medium text-gray-700">{script.last_run.by}</span> · {(script.last_run.modified_count ?? 0).toLocaleString()} {script.last_run.action === 'deleted' ? 'deleted' : 'updated'} · {script.last_run.scope === 'all' ? 'All Platforms' : (script.last_run.platform_name || script.last_run.scope)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">Never run</span>
                                )}
                              </p>
                            </div>

                            <div className="flex flex-wrap items-end gap-3">
                              {script.supports_platform_filter && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
                                  <select
                                    value={platformId}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setScriptPlatform(prev => ({ ...prev, [script.id]: v }));
                                      setScriptPreview(prev => ({ ...prev, [script.id]: undefined }));
                                    }}
                                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    data-testid={`data-script-platform-${script.id}`}
                                  >
                                    <option value="all">All Platforms</option>
                                    {settings.platforms.map((p) => (
                                      <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                    ))}
                                  </select>
                                </div>
                              )}

                              <button
                                onClick={() => handlePreviewScript(script.id)}
                                disabled={preview?.loading}
                                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5"
                                data-testid={`data-script-preview-${script.id}`}
                              >
                                {preview?.loading ? (
                                  <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-500"></div>Checking…</>
                                ) : (
                                  <><Search className="h-3.5 w-3.5" />Preview</>
                                )}
                              </button>

                              <button
                                onClick={() => setConfirmScript({ script, platformId, affected: preview?.affected_count })}
                                disabled={runningScript === script.id}
                                className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5"
                                data-testid={`data-script-run-${script.id}`}
                              >
                                {runningScript === script.id ? (
                                  <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>Running…</>
                                ) : (
                                  <><Play className="h-3.5 w-3.5" />Run</>
                                )}
                              </button>
                            </div>

                            {preview && !preview.loading && typeof preview.affected_count === 'number' && (
                              <div className={`text-sm rounded-md px-3 py-2 ${preview.affected_count > 0 ? (preview.action === 'delete' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800') : 'bg-green-50 text-green-700'}`} data-testid={`data-script-preview-result-${script.id}`}>
                                {preview.affected_count > 0
                                  ? <><strong>{preview.affected_count.toLocaleString()}</strong> document(s) would be {preview.action === 'delete' ? 'deleted' : 'updated'}{preview.platform_id === 'all' ? ' across all platforms' : ''}.</>
                                  : <>Nothing to fix — 0 matching documents{preview.platform_id === 'all' ? ' across all platforms' : ''}.</>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Close AI Insights by Date Range */}
              <Card className="border-sky-200" data-testid="close-insights-card">
                <CardHeader>
                  <CardTitle className="text-sky-700 flex items-center gap-2">
                    <Calendar className="h-5 w-5" /> Close AI Insights by Date Range
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Bulk-close <strong>new</strong> AI insight cards whose insight date falls within the selected range.
                    Only insights still in the <strong>new</strong> status are affected — already actioned or closed
                    insights are left untouched. Use <strong>Preview</strong> to see how many will be closed first.
                  </p>

                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Start date</label>
                      <input
                        type="date"
                        value={closeInsStart}
                        max={closeInsEnd || undefined}
                        onChange={(e) => { setCloseInsStart(e.target.value); setCloseInsPreview(null); }}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                        data-testid="close-insights-start-date"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">End date</label>
                      <input
                        type="date"
                        value={closeInsEnd}
                        min={closeInsStart || undefined}
                        onChange={(e) => { setCloseInsEnd(e.target.value); setCloseInsPreview(null); }}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                        data-testid="close-insights-end-date"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
                      <select
                        value={closeInsPlatform}
                        onChange={(e) => { setCloseInsPlatform(e.target.value); setCloseInsPreview(null); }}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                        data-testid="close-insights-platform"
                      >
                        <option value="all">All Platforms</option>
                        {settings.platforms.map((p) => (
                          <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                        ))}
                      </select>
                    </div>

                    <button
                      onClick={handlePreviewCloseInsights}
                      disabled={closeInsPreview?.loading || !closeInsStart || !closeInsEnd}
                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5"
                      data-testid="close-insights-preview-btn"
                    >
                      {closeInsPreview?.loading ? (
                        <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-500"></div>Checking…</>
                      ) : (
                        <><Search className="h-3.5 w-3.5" />Preview</>
                      )}
                    </button>

                    <button
                      onClick={() => setConfirmCloseIns(true)}
                      disabled={closingIns || !closeInsStart || !closeInsEnd}
                      className="px-3 py-1.5 text-sm bg-sky-600 text-white rounded-md hover:bg-sky-700 disabled:opacity-50 flex items-center gap-1.5"
                      data-testid="close-insights-run-btn"
                    >
                      {closingIns ? (
                        <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>Closing…</>
                      ) : (
                        <><CheckCircle2 className="h-3.5 w-3.5" />Close Insights</>
                      )}
                    </button>
                  </div>

                  {closeInsPreview && !closeInsPreview.loading && typeof closeInsPreview.affected_count === 'number' && (
                    <div className={`text-sm rounded-md px-3 py-2 ${closeInsPreview.affected_count > 0 ? 'bg-sky-50 text-sky-800' : 'bg-green-50 text-green-700'}`} data-testid="close-insights-preview-result">
                      {closeInsPreview.affected_count > 0
                        ? <><strong>{closeInsPreview.affected_count.toLocaleString()}</strong> new insight(s) would be closed{closeInsPlatform === 'all' ? ' across all platforms' : ''}.</>
                        : <>Nothing to close — 0 new insights in this range{closeInsPlatform === 'all' ? ' across all platforms' : ''}.</>}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Close insights confirmation */}
              {confirmCloseIns && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="close-insights-confirm-modal">
                  <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-sky-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Close AI insights?</h3>
                    </div>
                    <p className="text-sm text-gray-600">
                      This will set all <strong>new</strong> insights with an insight date between{' '}
                      <strong>{closeInsStart}</strong> and <strong>{closeInsEnd}</strong> to <strong>closed</strong>.
                    </p>
                    <div className="bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-sm text-sky-800">
                      Scope: <strong>{closeInsPlatform === 'all' ? 'All Platforms' : (settings.platforms.find(p => p.id === closeInsPlatform)?.name || closeInsPlatform)}</strong>
                      {typeof closeInsPreview?.affected_count === 'number'
                        ? <> · <strong>{closeInsPreview.affected_count.toLocaleString()}</strong> insight(s) will be closed.</>
                        : <> · Tip: click <strong>Preview</strong> first to see the impact.</>}
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setConfirmCloseIns(false)}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                        data-testid="close-insights-cancel-btn"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCloseInsights}
                        disabled={closingIns}
                        className="px-4 py-2 text-sm bg-sky-600 text-white rounded-md hover:bg-sky-700 disabled:opacity-50 flex items-center gap-1.5"
                        data-testid="close-insights-confirm-btn"
                      >
                        {closingIns ? 'Closing…' : 'Close Insights'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Maintenance script run confirmation */}
              {confirmScript && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="data-script-confirm-modal">
                  <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-5 w-5 text-amber-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Run maintenance script?</h3>
                    </div>
                    <p className="text-sm text-gray-800 font-medium">{confirmScript.script.name}</p>
                    <p className="text-sm text-gray-600">{confirmScript.script.description}</p>
                    <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm text-amber-800">
                      Scope: <strong>{confirmScript.platformId === 'all' ? 'All Platforms' : (settings.platforms.find(p => p.id === confirmScript.platformId)?.name || confirmScript.platformId)}</strong>
                      {typeof confirmScript.affected === 'number'
                        ? <> · <strong>{confirmScript.affected.toLocaleString()}</strong> document(s) will be {confirmScript.script.type === 'dedupe' ? 'permanently deleted' : 'updated'}.</>
                        : <> · Tip: click <strong>Preview</strong> first to see the impact.</>}
                      {confirmScript.script.type === 'dedupe' && (
                        <div className="mt-1 text-red-700 font-medium">⚠ This permanently deletes records and cannot be undone.</div>
                      )}
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => setConfirmScript(null)}
                        className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        data-testid="data-script-confirm-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRunScript}
                        disabled={!!runningScript}
                        className={`px-4 py-2 text-sm text-white rounded-md disabled:opacity-50 flex items-center gap-1.5 ${confirmScript.script.type === 'dedupe' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                        data-testid="data-script-confirm-run"
                      >
                        {runningScript ? (
                          <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>Running…</>
                        ) : (
                          <><Play className="h-3.5 w-3.5" />{confirmScript.script.type === 'dedupe' ? 'Delete Duplicates' : 'Run Script'}</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Warning Banner */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <h4 className="font-semibold text-red-900">Danger Zone</h4>
                    <p className="text-red-800 text-sm mt-1">
                      Actions in this section are destructive and cannot be undone. 
                      Please proceed with caution.
                    </p>
                  </div>
                </div>
              </div>

              {/* Generate Sample Data Section */}
              <Card className="border-emerald-200" data-testid="generate-sample-data-card">
                <CardHeader>
                  <CardTitle className="text-emerald-700 flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Generate Sample Data
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Create realistic sample transactions for a platform + month (statuses, retries, channels,
                    currencies) following the sample-data rules. Merchant and customer totals are recalculated
                    automatically. <strong className="text-amber-700">Any existing transactions for the selected
                    month + platform are replaced.</strong>
                  </p>

                  <div className="space-y-4">
                    {/* Platform */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Platform</label>
                      <select
                        value={genPlatformId || ''}
                        onChange={(e) => { setGenPlatformId(e.target.value || null); setGenConfirm(false); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        data-testid="gen-platform-select"
                      >
                        <option value="">Select a platform...</option>
                        {settings.platforms.map((platform) => (
                          <option key={platform.id} value={platform.id}>
                            {platform.name} ({platform.id})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Year + Month + Count */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                        <select
                          value={genYear}
                          onChange={(e) => setGenYear(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          data-testid="gen-year-select"
                        >
                          {Array.from({ length: 6 }, (_, i) => {
                            const year = new Date().getFullYear() - 3 + i;
                            return <option key={year} value={year}>{year}</option>;
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                        <select
                          value={genMonth}
                          onChange={(e) => setGenMonth(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          data-testid="gen-month-select"
                        >
                          {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                            <option key={i + 1} value={i + 1}>{m}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Transactions</label>
                        <input
                          type="number"
                          min={50}
                          max={20000}
                          step={100}
                          value={genCount}
                          onChange={(e) => setGenCount(Math.max(50, Math.min(20000, parseInt(e.target.value) || 0)))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          data-testid="gen-count-input"
                        />
                      </div>
                    </div>

                    {/* Channel mix */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Channel Mix (weights, normalized automatically)
                      </label>
                      <div className="grid grid-cols-3 gap-4">
                        {['web', 'pos', 'recurring'].map((ch) => (
                          <div key={ch}>
                            <span className="text-xs text-gray-500 capitalize">{ch}</span>
                            <input
                              type="number"
                              min={0}
                              value={genChannels[ch]}
                              onChange={(e) => setGenChannels(prev => ({ ...prev, [ch]: Math.max(0, parseInt(e.target.value) || 0) }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                              data-testid={`gen-channel-${ch}`}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Total weight: {(Number(genChannels.web) || 0) + (Number(genChannels.pos) || 0) + (Number(genChannels.recurring) || 0)}
                      </p>
                    </div>

                    {genPlatformId && (
                      <div className="bg-emerald-50 rounded-lg p-4 space-y-4">
                        <div className="text-sm">
                          <p className="font-medium text-gray-900">You are about to generate sample data for:</p>
                          <p className="text-emerald-700 font-bold text-lg mt-1">
                            {settings.platforms.find(p => p.id === genPlatformId)?.name} — {genYear}-{String(genMonth).padStart(2, '0')}
                          </p>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded p-3">
                          <p className="text-sm text-amber-800">
                            This will <strong>delete existing {genYear}-{String(genMonth).padStart(2, '0')} transactions</strong> for
                            this platform, insert ~{genCount} new ones, and recalculate merchant &amp; customer totals.
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="genConfirm"
                            checked={genConfirm}
                            onChange={(e) => setGenConfirm(e.target.checked)}
                            className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                            data-testid="gen-confirm-checkbox"
                          />
                          <label htmlFor="genConfirm" className="text-sm text-gray-700">
                            I understand existing data for this month will be replaced
                          </label>
                        </div>

                        {/* Progress */}
                        {genJob && (genJob.status === 'pending' || genJob.status === 'running') && (
                          <div className="bg-white border border-emerald-200 rounded-lg p-4" data-testid="gen-progress">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-emerald-800">{genJob.message || 'Working…'}</span>
                              <span className="text-sm text-emerald-700">{genJob.progress || 0}%</span>
                            </div>
                            <div className="w-full bg-emerald-100 rounded-full h-2">
                              <div className="bg-emerald-600 h-2 rounded-full transition-all" style={{ width: `${genJob.progress || 0}%` }}></div>
                            </div>
                          </div>
                        )}

                        <button
                          onClick={submitGenerateSampleData}
                          disabled={generating || !genConfirm}
                          className="w-full px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          data-testid="gen-submit-btn"
                        >
                          {generating ? (
                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Generating…</>
                          ) : (
                            <><Play className="h-4 w-4" />Generate Sample Data</>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Recent generations */}
                    {lastGenJobs.length > 0 && (
                      <div className="border-t border-gray-100 pt-3" data-testid="gen-last-jobs">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Recent generations</p>
                        <div className="space-y-1.5">
                          {lastGenJobs.map((j) => (
                            <div key={j.id} className="flex items-center justify-between text-xs text-gray-600" data-testid={`gen-job-${j.id}`}>
                              <span className="truncate">
                                <span className="font-medium">{j.platform_name}</span> · {j.month}
                                {j.summary ? ` · ${j.summary.inserted_count} txns` : ''}
                              </span>
                              <span className={`ml-2 shrink-0 px-1.5 py-0.5 rounded ${j.status === 'completed' ? 'bg-green-50 text-green-700' : j.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                                {j.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Delete Platform Data Section */}
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="text-red-600 flex items-center gap-2">
                    <Trash2 className="h-5 w-5" />
                    Delete Platform Data
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Delete all data (transactions, customers, merchants, and logs) for a specific platform.
                    This action is permanent and cannot be reversed.
                  </p>
                  
                  {/* Platform Selection */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Platform
                      </label>
                      <select
                        value={deletePlatformId || ''}
                        onChange={(e) => {
                          setDeletePlatformId(e.target.value || null);
                          setDeleteConfirmText('');
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="">Select a platform...</option>
                        {settings.platforms.map((platform) => (
                          <option key={platform.id} value={platform.id}>
                            {platform.name} ({platform.id})
                          </option>
                        ))}
                      </select>
                    </div>

                    {deletePlatformId && (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                        <div className="text-sm">
                          <p className="font-medium text-gray-900">
                            You are about to delete all data for:
                          </p>
                          <p className="text-red-600 font-bold text-lg mt-1">
                            {settings.platforms.find(p => p.id === deletePlatformId)?.name}
                          </p>
                        </div>
                        
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                          <p className="text-sm text-yellow-800">
                            <strong>This will permanently delete:</strong>
                          </p>
                          <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                            <li>All transactions</li>
                            <li>All customers</li>
                            <li>All merchants</li>
                            <li>All audit logs</li>
                          </ul>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type the platform name to confirm: <span className="text-red-600 font-bold">{settings.platforms.find(p => p.id === deletePlatformId)?.name}</span>
                          </label>
                          <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="Type platform name here..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                            disabled={deletingPlatformData}
                          />
                        </div>

                        {/* Background Job Progress Indicator */}
                        {deletionJob && deletionJob.status !== 'completed' && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center gap-3">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                              <div>
                                <p className="font-medium text-blue-900">Deletion in progress...</p>
                                <p className="text-sm text-blue-700 mt-1">
                                  Large dataset detected. Deleting {deletionJob.expected_counts?.transactions?.toLocaleString() || '?'} transactions, {deletionJob.expected_counts?.customers?.toLocaleString() || '?'} customers, and {deletionJob.expected_counts?.merchants?.toLocaleString() || '?'} merchants.
                                </p>
                                <p className="text-xs text-blue-600 mt-2">Job ID: {deletionJob.id}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        <button
                          onClick={() => handleDeletePlatformData(
                            deletePlatformId, 
                            settings.platforms.find(p => p.id === deletePlatformId)?.name
                          )}
                          disabled={deletingPlatformData || deleteConfirmText !== settings.platforms.find(p => p.id === deletePlatformId)?.name}
                          className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {deletingPlatformData ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              {deletionJob ? 'Processing Large Dataset...' : 'Deleting...'}
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4" />
                              Delete All Platform Data
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Delete Monthly Data Section */}
              <Card className="border-orange-200">
                <CardHeader>
                  <CardTitle className="text-orange-600 flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Delete Monthly Data
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Delete transactions for a specific month and platform. Merchant and customer totals will be
                    automatically recalculated based on the remaining data. This is useful for removing test data
                    or correcting a specific period without affecting other months.
                  </p>
                  
                  <div className="space-y-4">
                    {/* Platform Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Platform
                      </label>
                      <select
                        value={monthlyDeletePlatformId || ''}
                        onChange={(e) => {
                          setMonthlyDeletePlatformId(e.target.value || null);
                          setMonthlyDeleteConfirm(false);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">Select a platform...</option>
                        {settings.platforms.map((platform) => (
                          <option key={platform.id} value={platform.id}>
                            {platform.name} ({platform.id})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Year and Month Selection */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Year
                        </label>
                        <select
                          value={monthlyDeleteYear}
                          onChange={(e) => setMonthlyDeleteYear(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                          {Array.from({ length: 5 }, (_, i) => {
                            const year = new Date().getFullYear() - i;
                            return (
                              <option key={year} value={year}>{year}</option>
                            );
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Month
                        </label>
                        <select
                          value={monthlyDeleteMonth}
                          onChange={(e) => setMonthlyDeleteMonth(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                          <option value="1">January</option>
                          <option value="2">February</option>
                          <option value="3">March</option>
                          <option value="4">April</option>
                          <option value="5">May</option>
                          <option value="6">June</option>
                          <option value="7">July</option>
                          <option value="8">August</option>
                          <option value="9">September</option>
                          <option value="10">October</option>
                          <option value="11">November</option>
                          <option value="12">December</option>
                        </select>
                      </div>
                    </div>

                    {monthlyDeletePlatformId && (
                      <div className="bg-orange-50 rounded-lg p-4 space-y-4">
                        <div className="text-sm">
                          <p className="font-medium text-gray-900">
                            You are about to delete data for:
                          </p>
                          <p className="text-orange-600 font-bold text-lg mt-1">
                            {settings.platforms.find(p => p.id === monthlyDeletePlatformId)?.name} - {monthlyDeleteYear}-{String(monthlyDeleteMonth).padStart(2, '0')}
                          </p>
                        </div>
                        
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                          <p className="text-sm text-yellow-800">
                            <strong>This will:</strong>
                          </p>
                          <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                            <li>Delete all transactions for the selected month</li>
                            <li>Recalculate merchant totals from remaining data</li>
                            <li>Recalculate customer totals from remaining data</li>
                          </ul>
                        </div>

                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="monthlyDeleteConfirm"
                            checked={monthlyDeleteConfirm}
                            onChange={(e) => setMonthlyDeleteConfirm(e.target.checked)}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                          />
                          <label htmlFor="monthlyDeleteConfirm" className="text-sm text-gray-700">
                            I understand this action cannot be undone
                          </label>
                        </div>

                        <button
                          onClick={handleDeleteMonthlyData}
                          disabled={deletingMonthlyData || !monthlyDeleteConfirm}
                          className="w-full px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {deletingMonthlyData ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Deleting & Recalculating...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4" />
                              Delete Monthly Data & Recalculate Totals
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recalculate Totals Section */}
              <Card className="border-blue-200">
                <CardHeader>
                  <CardTitle className="text-blue-600 flex items-center gap-2">
                    <RefreshCcw className="h-5 w-5" />
                    Recalculate Totals
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Recalculate merchant and customer totals from transaction data. Use this if totals appear
                    incorrect after data imports, manual edits, or system issues. This does not delete any data.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Platform
                      </label>
                      <select
                        value={recalcPlatformId || ''}
                        onChange={(e) => setRecalcPlatformId(e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="recalc-platform-select"
                      >
                        <option value="">Select a platform...</option>
                        {settings.platforms.map((platform) => (
                          <option key={platform.id} value={platform.id}>
                            {platform.name} ({platform.id})
                          </option>
                        ))}
                      </select>
                    </div>

                    {recalcPlatformId && (
                      <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                        <p className="text-sm text-blue-800">
                          This will recalculate <strong>total sales</strong>, <strong>transaction count</strong>, and <strong>unique customers</strong> for all merchants,
                          plus <strong>total spent</strong> and <strong>approved totals</strong> for all customers in:
                        </p>
                        <p className="text-blue-700 font-bold text-lg">
                          {settings.platforms.find(p => p.id === recalcPlatformId)?.name}
                        </p>
                        
                        <button
                          onClick={handleRecalculateTotals}
                          disabled={recalculating}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          data-testid="recalc-btn"
                        >
                          {recalculating ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Recalculating...
                            </>
                          ) : (
                            <>
                              <RefreshCcw className="h-4 w-4" />
                              Recalculate Totals
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Deletion History Section */}
              <Card className="border-gray-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-gray-700 flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Deletion History
                    </CardTitle>
                    <button
                      onClick={fetchDeletionHistory}
                      disabled={loadingHistory}
                      className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md flex items-center gap-1"
                    >
                      <RefreshCcw className={`h-4 w-4 ${loadingHistory ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingHistory ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <p className="mt-2 text-sm text-gray-600">Loading history...</p>
                    </div>
                  ) : deletionHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Trash2 className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>No deletion history found</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date & Time
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Platform
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Deleted By
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Transactions
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Customers
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Merchants
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {deletionHistory.map((item, index) => (
                            <tr key={item.id || index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {new Date(item.created_at || item.timestamp).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{item.platform_name}</div>
                                  <div className="text-xs text-gray-500">{item.platform_id}</div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {item.user_email}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                                {(item.deleted_counts?.transactions || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {(item.deleted_counts?.customers || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {(item.deleted_counts?.merchants || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  item.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  item.status === 'running' ? 'bg-blue-100 text-blue-800' :
                                  item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  item.status === 'failed' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {item.status === 'running' && (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                                  )}
                                  {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {activeTab === 'platforms' ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Icon
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Platform ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Platform URL
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Timezone
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created At
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        {activeTab === 'result_codes' && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Result Status
                          </th>
                        )}
                        {activeTab === 'currencies' && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Symbol
                          </th>
                        )}
                        {activeTab === 'countries' && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Flag
                          </th>
                        )}
                        {(activeTab === 'payment_methods' || activeTab === 'processors') && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Logo URL
                          </th>
                        )}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Active
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === 'platforms' ? 9 : (activeTab === 'result_codes' ? 5 : (activeTab === 'currencies' || activeTab === 'countries') ? 5 : 4)} className="px-6 py-8 text-center text-gray-500">
                        {searchTerm ? 'No items match your search.' : `No ${activeTab.replace('_', ' ')} found. Click "Add New" to create one.`}
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        {activeTab === 'platforms' ? (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {item.favicon_url ? (
                                <img 
                                  src={item.favicon_url} 
                                  alt="" 
                                  className="h-6 w-6 rounded"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="h-6 w-6 bg-gray-100 rounded flex items-center justify-center">
                                  <span className="text-xs text-gray-400">🌐</span>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 bg-gray-50">
                              {item.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {item.name}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                              {item.platform_url ? (
                                <a href={item.platform_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                  {item.platform_url}
                                </a>
                              ) : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {item.timezone || 'UTC'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                              {item.description || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(item.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <button
                                onClick={() => handleToggleActive(item)}
                                data-testid={`platform-row-toggle-${item.id}`}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                style={{
                                  backgroundColor: item.active 
                                    ? (tempTheme.successColor || theme.successColor) + '20'
                                    : (tempTheme.errorColor || theme.errorColor) + '20',
                                  color: item.active 
                                    ? (tempTheme.successColor || theme.successColor)
                                    : (tempTheme.errorColor || theme.errorColor)
                                }}
                              >
                                {item.active ? (
                                  <ToggleRight className="h-3 w-3 mr-1" />
                                ) : (
                                  <ToggleLeft className="h-3 w-3 mr-1" />
                                )}
                                {item.active ? 'Active' : 'Inactive'}
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleEdit(item)}
                                  data-testid={`platform-row-edit-${item.id}`}
                                  className="hover:opacity-80 transition-opacity"
                                  style={{ color: tempTheme.linkColor || theme.linkColor }}
                                  title="Edit"
                                  type="button"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(item)}
                                  data-testid={`platform-row-delete-${item.id}`}
                                  className="hover:opacity-80 transition-opacity"
                                  style={{ color: tempTheme.errorColor || theme.errorColor }}
                                  title="Delete"
                                  type="button"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {item.code}
                              {activeTab === 'countries' && item.alpha3 && (
                                <span className="ml-2 text-xs font-normal text-gray-400" data-testid={`country-alpha3-${item.code}`}>
                                  {item.alpha3}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {activeTab === 'channels' ? (
                                <div className="flex items-center gap-2">
                                  {(() => {
                                    const { icon: Icon, color, bg } = getChannelIcon(item.code);
                                    return (
                                      <div className={`p-1.5 rounded-md ${bg}`}>
                                        <Icon className={`h-4 w-4 ${color}`} />
                                      </div>
                                    );
                                  })()}
                                  <span>{item.name}</span>
                                </div>
                              ) : (
                                item.name
                              )}
                            </td>
                            {activeTab === 'result_codes' && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {(() => {
                                  // Find the status name from the statuses list
                                  const statusObj = settings.statuses?.find(s => s.code === item.status);
                                  const statusName = statusObj?.name || item.status || 'Unknown';
                                  
                                  // Determine badge color based on status code
                                  const isCompleted = item.status === 'completed';
                                  const isFailed = item.status?.startsWith('failed');
                                  
                                  let badgeClass = 'bg-gray-100 text-gray-800'; // default
                                  if (isCompleted) {
                                    badgeClass = 'bg-green-100 text-green-800';
                                  } else if (isFailed) {
                                    badgeClass = 'bg-red-100 text-red-800';
                                  } else if (item.status === 'pending' || item.status === 'processing' || item.status === 'initiated') {
                                    badgeClass = 'bg-yellow-100 text-yellow-800';
                                  } else if (item.status === 'refunded') {
                                    badgeClass = 'bg-blue-100 text-blue-800';
                                  }
                                  
                                  return (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
                                      {statusName}
                                    </span>
                                  );
                                })()}
                              </td>
                            )}
                            {activeTab === 'currencies' && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.symbol}
                              </td>
                            )}
                            {activeTab === 'countries' && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <span 
                                  className={`fi fi-${item.code?.toLowerCase()}`} 
                                  style={{ fontSize: '1.5rem' }}
                                  title={item.name}
                                />
                              </td>
                            )}
                            {(activeTab === 'payment_methods' || activeTab === 'processors') && (
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {(() => {
                                  // Map codes to local images (automatically show for common cards)
                                  const localImageMap = {
                                    'visa': '/images/payment-methods/visa.png',
                                    'mastercard': '/images/payment-methods/mastercard.png',
                                    'amex': '/images/payment-methods/amex.png'
                                  };
                                  
                                  const localImage = localImageMap[item.code?.toLowerCase()];
                                  
                                  // Priority: 1) logo_url from DB, 2) local image map, 3) no logo
                                  if (item.logo_url) {
                                    return (
                                      <div className="flex items-center gap-2">
                                        <img 
                                          src={item.logo_url}
                                          alt={item.name}
                                          className="h-5 w-auto object-contain"
                                          style={{ maxWidth: '50px' }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                        <span className="text-xs text-blue-600 font-medium">
                                          {item.logo_url.startsWith('/images/') ? 'Local' : 'URL'}
                                        </span>
                                      </div>
                                    );
                                  } else if (localImage) {
                                    return (
                                      <div className="flex items-center gap-2">
                                        <img 
                                          src={localImage}
                                          alt={item.name}
                                          className="h-5 w-auto object-contain"
                                          style={{ maxWidth: '50px' }}
                                        />
                                        <span className="text-xs text-green-600 font-medium">Auto</span>
                                      </div>
                                    );
                                  } else {
                                    return <span className="text-gray-400">No logo</span>;
                                  }
                                })()}
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <button
                                onClick={() => handleToggleActive(item)}
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  item.is_active
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {item.is_active ? (
                                  <ToggleRight className="h-3 w-3 mr-1" />
                                ) : (
                                  <ToggleLeft className="h-3 w-3 mr-1" />
                                )}
                                {item.is_active ? 'Active' : 'Inactive'}
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleEdit(item)}
                                  className="hover:opacity-80 transition-opacity"
                                  style={{ color: tempTheme.linkColor || theme.linkColor }}
                                  title="Edit"
                                  type="button"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(item)}
                                  className="hover:opacity-80 transition-opacity"
                                  style={{ color: tempTheme.errorColor || theme.errorColor }}
                                  title="Delete"
                                  type="button"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}


      {/* Modal for Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">
              {editingItem ? 'Edit' : 'Add New'} {({
                currencies: 'Currency', countries: 'Country', statuses: 'Status',
                payment_methods: 'Payment Method', processors: 'Processor',
                transaction_types: 'Transaction Type', result_codes: 'Result Code',
                risk_actions: 'Risk Action', channels: 'Channel', platforms: 'Platform'
              })[activeTab] || activeTab.replace('_', ' ').replace(/s$/, '')}
            </h2>
            <div className="space-y-4">
              {activeTab === 'platforms' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Platform Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter platform name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Platform URL
                    </label>
                    <input
                      type="url"
                      value={formData.platform_url || ''}
                      onChange={(e) => setFormData({ ...formData, platform_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://example.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">Favicon will be auto-fetched from this URL</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timezone
                    </label>
                    <select
                      value={formData.timezone || 'UTC'}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <optgroup label="Americas">
                        <option value="America/New_York">America/New_York (EST/EDT)</option>
                        <option value="America/Chicago">America/Chicago (CST/CDT)</option>
                        <option value="America/Denver">America/Denver (MST/MDT)</option>
                        <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                        <option value="America/Mexico_City">America/Mexico_City (CST)</option>
                        <option value="America/Puerto_Rico">America/Puerto_Rico (AST)</option>
                        <option value="America/Bogota">America/Bogota (COT)</option>
                        <option value="America/Lima">America/Lima (PET)</option>
                        <option value="America/Santiago">America/Santiago (CLT)</option>
                        <option value="America/Buenos_Aires">America/Buenos_Aires (ART)</option>
                        <option value="America/Sao_Paulo">America/Sao_Paulo (BRT)</option>
                        <option value="America/Toronto">America/Toronto (EST/EDT)</option>
                        <option value="America/Vancouver">America/Vancouver (PST/PDT)</option>
                      </optgroup>
                      <optgroup label="Europe">
                        <option value="Europe/London">Europe/London (GMT/BST)</option>
                        <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                        <option value="Europe/Berlin">Europe/Berlin (CET/CEST)</option>
                        <option value="Europe/Madrid">Europe/Madrid (CET/CEST)</option>
                        <option value="Europe/Rome">Europe/Rome (CET/CEST)</option>
                        <option value="Europe/Amsterdam">Europe/Amsterdam (CET/CEST)</option>
                      </optgroup>
                      <optgroup label="Asia/Pacific">
                        <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                        <option value="Asia/Shanghai">Asia/Shanghai (CST)</option>
                        <option value="Asia/Hong_Kong">Asia/Hong_Kong (HKT)</option>
                        <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                        <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                        <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                        <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                        <option value="Pacific/Auckland">Pacific/Auckland (NZST/NZDT)</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option value="UTC">UTC (Coordinated Universal Time)</option>
                      </optgroup>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Platform's local timezone for reporting</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter platform description"
                      rows="3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Supported Countries
                    </label>
                    <input
                      type="text"
                      value={formData.countries?.join(', ') || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        countries: e.target.value.split(',').map(c => c.trim()).filter(c => c)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter country codes separated by commas (e.g., US, CA, MX)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Supported Currencies
                    </label>
                    <input
                      type="text"
                      value={formData.currencies?.join(', ') || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        currencies: e.target.value.split(',').map(c => c.trim()).filter(c => c)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter currency codes separated by commas (e.g., USD, CAD, MXN)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Currency
                    </label>
                    <select
                      data-testid="platform-default-currency-select"
                      value={formData.default_currency || ''}
                      onChange={(e) => setFormData({ ...formData, default_currency: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">No default (All Currencies)</option>
                      {(settings.currencies || [])
                        .filter(c => c.is_active !== false)
                        .map(c => (
                          <option key={c.id || c.code} value={c.code}>
                            {c.code}{c.name ? ` - ${c.name}` : ''}
                          </option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Used as the default currency filter across all dashboard pages for this platform
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code *
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter code"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter name"
                      required
                    />
                  </div>
                  {activeTab === 'result_codes' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status *
                      </label>
                      <select
                        value={formData.status || 'failed_processor'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        {settings.statuses && settings.statuses.length > 0 ? (
                          settings.statuses
                            .filter(s => s.is_active !== false)
                            .map(status => (
                              <option key={status.id || status.code} value={status.code}>
                                {status.name}
                              </option>
                            ))
                        ) : (
                          <>
                            <option value="completed">Completed</option>
                            <option value="failed_processor">Failed Processor</option>
                          </>
                        )}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Select the transaction status for this result code
                      </p>
                    </div>
                  )}
                  {activeTab === 'currencies' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Symbol
                      </label>
                      <input
                        type="text"
                        value={formData.symbol}
                        onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., $, €, £"
                      />
                    </div>
                  )}
                  {activeTab === 'countries' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alpha-3 Code
                      </label>
                      <input
                        type="text"
                        maxLength={3}
                        value={formData.alpha3 || ''}
                        onChange={(e) => setFormData({ ...formData, alpha3: e.target.value.toUpperCase() })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 uppercase"
                        placeholder="e.g., USA, GBR, DEU"
                        data-testid="country-alpha3-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave blank to auto-derive from the 2-letter code.</p>
                    </div>
                  )}
                  {(activeTab === 'payment_methods' || activeTab === 'processors') && (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Logo URL or Upload Image
                      </label>
                      
                      {/* URL Input */}
                      <input
                        type="url"
                        value={formData.logo_url || ''}
                        onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://example.com/logo.png"
                      />
                      
                      {/* OR separator */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 border-t border-gray-300"></div>
                        <span className="text-xs text-gray-500">OR</span>
                        <div className="flex-1 border-t border-gray-300"></div>
                      </div>
                      
                      {/* File Upload */}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Validate file size (max 2MB)
                            if (file.size > 2 * 1024 * 1024) {
                              toast({
                                title: 'File too large',
                                description: 'Please upload an image smaller than 2MB',
                                variant: 'destructive'
                              });
                              e.target.value = '';
                              return;
                            }
                            
                            // Upload to backend
                            try {
                              // Check if code exists
                              if (!formData.code) {
                                toast({
                                  title: 'Code required',
                                  description: 'Please enter a payment method code before uploading an image',
                                  variant: 'destructive'
                                });
                                e.target.value = '';
                                return;
                              }
                              
                              const uploadFormData = new FormData();
                              uploadFormData.append('file', file);
                              uploadFormData.append('code', formData.code);
                              
                              const response = await fetch(`${backendUrl}/api/upload/payment-method-logo`, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${token}`
                                },
                                body: uploadFormData
                              });
                              
                              if (response.ok) {
                                const data = await response.json();
                                setFormData(prev => ({...prev, logo_url: data.logo_url}));
                                toast({
                                  title: 'Image uploaded',
                                  description: `Logo saved as ${data.filename}`
                                });
                              } else {
                                const error = await response.json();
                                throw new Error(error.detail || 'Upload failed');
                              }
                            } catch (error) {
                              console.error('Upload error:', error);
                              toast({
                                title: 'Upload failed',
                                description: error.message,
                                variant: 'destructive'
                              });
                              e.target.value = '';
                            }
                          }
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                      />
                      
                      {/* Preview */}
                      {formData.logo_url && (
                        <div className="mt-2 p-2 border rounded bg-gray-50">
                          <p className="text-xs text-gray-600 mb-2">Preview:</p>
                          <img 
                            src={formData.logo_url}
                            alt="Logo preview" 
                            className="h-8 w-auto object-contain" 
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-500">
                        Enter a URL or upload an image (max 2MB). Images are saved to server storage like Visa/Mastercard logos.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                style={{ 
                  backgroundColor: tempTheme.buttonSecondaryColor || theme.buttonSecondaryColor,
                  color: tempTheme.buttonSecondaryTextColor || theme.textColor
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = tempTheme.hoverBackgroundColor || theme.hoverBackgroundColor}
                onMouseLeave={(e) => e.target.style.backgroundColor = tempTheme.buttonSecondaryColor || theme.buttonSecondaryColor}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                data-testid="settings-modal-save-button"
                disabled={activeTab === 'platforms' ? !formData.name : (!formData.code || !formData.name)}
                className="px-4 py-2 text-sm font-medium disabled:bg-gray-400 rounded-md transition-colors"
                style={{ 
                  backgroundColor: (activeTab === 'platforms' ? !formData.name : (!formData.code || !formData.name)) ? '#9CA3AF' : (tempTheme.buttonColor || theme.buttonColor),
                  color: tempTheme.buttonTextColor || theme.buttonTextColor || '#ffffff'
                }}
                onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = tempTheme.buttonHoverColor || theme.buttonHoverColor)}
                onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = tempTheme.buttonColor || theme.buttonColor)}
              >
                {editingItem ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;